import { omit } from 'lodash'

import { t as translate } from 'plottr_locales'

import {
  newFileHierarchies as createNewFileHierarchies,
  newFileCategories as createNewFileCategories,
} from '../store/newFileState'
import {
  customAttributes as defaultCustomAttributes,
  hierarchyLevel as createDefaultHierarchyLevel,
} from '../store/initialState'

/**
 * @template A
 * @typedef {import('./plottr').Importable<A>} Importable
 */

/**
 * @typedef {import('../index').Book} Book
 * @typedef {import('./system').CustomTemplate} CustomTemplate
 * @typedef {import('./system').CharacterTemplateAttribute} CharacterTemplateAttribute
 * @typedef {import('./system').SceneTemplateAttribute} SceneTemplateAttribute
 * @typedef {import('./system').PlotlineCustomTemplateData} PlotlineCustomTemplateData
 * @typedef {import('./system').CharacterCustomTemplateAttribute} CharacterCustomTemplateAttribute
 * @typedef {import('./system').SceneCustomTemplateAttribute} SceneCustomTemplateAttribute
 * @typedef {import('./system').MergeBias} MergeBias
 * @typedef {import('./system').BuiltInTemplate} BuiltInTemplate
 * @typedef {import('./system').ProjectTemplateData} ProjectTemplateData
 * @typedef {import('./system').PlotlineTemplateData} PlotlineTemplateData
 * @typedef {import('./system').PlotlineTemplate} PlotlineTemplate
 * @typedef {import('./system').SceneTemplate} SceneTemplate
 * @typedef {import('./system').ProjectTemplate} ProjectTemplate
 * @typedef {import('./system').TemplateCategory} TemplateCategory
 * @typedef {import('./plottr').PlottrFileCard} PlottrFileCard
 * @typedef {import('./plottr').DashboardModalUi} DashboardModalUi
 * @typedef {import('./plottr').Beat} Beat
 * @typedef {import('./plottr').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('./plottr').PlottrFileBeats} PlottrFileBeats
 * @typedef {import('./plottr').Line} Line
 * @typedef {import('./plottr').ThemeHierarchyColors} ThemeHierarchyColors
 * @typedef {import('./plottr').HierarchyLevel} HierarchyLevel
 * @typedef {import('./plottr').HierarchyLevels} HierarchyLevels
 * @typedef {import('./plottr').HierarchyLevelsPlottrFile} HierarchyLevelsPlottrFile
 * @typedef {import('./plottr').PlottrFileBooks} PlottrFileBooks
 * @typedef {import('./plottr').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('./plottr').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('./plottr').TagsPlottrFileEntry} TagsPlottrFileEntry
 * @typedef {import('./plottr').NotesPlottrFileEntry} NotesPlottrFileEntry
 * @typedef {import('./plottr').Category} Category
 * @typedef {import('./plottr').CategoriesPlottrFile} CategoriesPlottrFile
 * @typedef {import('./plottr').ImageRecord} ImageRecord
 * @typedef {import('./plottr').PlottrFileImages} PlottrFileImages
 * @typedef {import('./plottr').CharactersPlottrFileEntry} CharactersPlottrFileEntry
 * @typedef {import('./plottr').Attributes} Attributes
 * @typedef {import('./plottr').PlacesPlottrFileEntry} PlacesPlottrFileEntry
 * @typedef {import('./plottr').PlottrFileAudioClips} PlottrFileAudioClips
 * @typedef {import('./plottr').PlottrFileFamilyTrees} PlottrFileFamilyTrees
 * @typedef {import('./plottr').PlottrFileSeries} PlottrFileSeries
 * @typedef {import('./plottr').PlottrFileUi} PlottrFileUi
 * @typedef {import('./plottr').FeatureFlags} FeatureFlags
 * @typedef {import('./plottr').PlottrFileFile} PlottrFileFile
 * @typedef {import('./plottr').PlottrFile} PlottrFile
 * @typedef {import('./plottr').PlottrTab} PlottrTab
 * @typedef {import('./plottr').TimelineOrientation} TimelineOrientation
 * @typedef {import('./plottr').CharacterFilter} CharacterFilter
 * @typedef {import('./plottr').PlaceFilter} PlaceFilter
 * @typedef {import('./plottr').NoteFilter} NoteFilter
 * @typedef {import('./plottr').TimelineFilter} TimelineFilter
 * @typedef {import('./plottr').TimelineScrollPosition} TimelineScrollPosition
 * @typedef {import('./plottr').PinnedPlotlines} PinnedPlotlines
 * @typedef {import('./plottr').TimelineUi} TimelineUi
 * @typedef {import('./plottr').TimelineSize} TimelineSize
 * @typedef {import('./plottr').ActView} ActView
 * @typedef {import('./plottr').Focus} Focus
 * @typedef {import('./plottr').Selection} Selection
 * @typedef {import('./plottr').DOMSelection} DOMSelection
 * @typedef {import('./plottr').FocusPath} FocusPath
 * @typedef {import('./plottr').Point} Point
 * @typedef {import('./plottr').SlateFocusPath} SlateFocusPath
 * @typedef {import('./plottr').SearchTerms} SearchTerms
 * @typedef {import('./plottr').AttributeTab} AttributeTab
 * @typedef {import('./plottr').CharacterTabUi} CharacterTabUi
 * @typedef {import('./plottr').CharacterTemplate} CharacterTemplate
 * @typedef {import('./plottr').CharacterCustomTemplate} CharacterCustomTemplate
 * @typedef {import('./plottr').CharacterTabCharacterEditor} CharacterTabCharacterEditor
 * @typedef {import('./plottr').CustomAttributeOrderUiPlottrFile} CustomAttributeOrderUiPlottrFile
 * @typedef {import('./plottr').AttributeOrderEntry} AttributeOrderEntry
 * @typedef {import('./plottr').CardDialogUi} CardDialogUi
 * @typedef {import('./plottr').UiCollaborators} UiCollaborators
 * @typedef {import('./plottr').CollaboratorUIState} CollaboratorUIState
 * @typedef {import('./plottr').BookDialog} BookDialog
 * @typedef {import('./plottr').SearchDialogUi} SearchDialogUi
 * @typedef {import('./plottr').SearchHit} SearchHit
 * @typedef {import('./plottr').SearchDialogActiveTab} SearchDialogActiveTab
 * @typedef {import('./plottr').ProjectTabUi} ProjectTabUi
 * @typedef {import('./plottr').OutlineTabUi} OutlineTabUi
 * @typedef {import('./plottr').OutlineCardEditor} OutlineCardEditor
 * @typedef {import('./plottr').OutlineTabUiLayout} OutlineTabUiLayout
 * @typedef {import('./plottr').OutlineTabView} OutlineTabView
 * @typedef {import('./plottr').OutlineFulltextUiLayout} OutlineFulltextUiLayout
 * @typedef {import('./plottr').OutlineTabCardUiLayout} OutlineTabCardUiLayout
 * @typedef {import('./plottr').OutlineFulltextHoverDetails} OutlineFulltextHoverDetails
 * @typedef {import('./plottr').NoteTabUi} NoteTabUi
 * @typedef {import('./plottr').PlaceTabUi} PlaceTabUi
 * @typedef {import('./plottr').TagTabUi} TagTabUi
 * @typedef {import('./plottr').TemplateModalUi} TemplateModalUi
 * @typedef {import('./plottr').ActConfigModalUi} ActConfigModalUi
 * @typedef {import('./plottr').ResturctureTimelineModalUi} ResturctureTimelineModalUi
 * @typedef {import('./plottr').ConflictResolutionModalUi} ConflictResolutionModalUi
 * @typedef {import('./plottr').ImportModalUi} ImportModalUi
 * @typedef {import('./plottr').ImportModalUiData} ImportModalUiData
 * @typedef {import('./plottr').PlottrFileBook} PlottrFileBook
 * @typedef {import('./plottr').ImportableLegacyCustomAttributes} ImportableLegacyCustomAttributes
 * @typedef {import('./plottr').ImportModalUiBookData} ImportModalUiBookData
 * @typedef {import('./plottr').FamilyTreeUi} FamilyTreeUi
 * @typedef {import('./plottr').FamilyTreeStagePositions} FamilyTreeStagePositions
 * @typedef {import('./plottr').FamilyTreeStagePosition} FamilyTreeStagePosition
 * @typedef {import('./plottr').DocumentationUi} DocumentationUi
 * @typedef {import('./plottr').PreviewPanelItem} PreviewPanelItem
 * @typedef {import('./plottr').PreviewPanelCoord} PreviewPanelCoord
 * @typedef {import('./plottr').PreviewPanels} PreviewPanels
 * @typedef {import('./plottr').QuickEditModal} QuickEditModal
 * @typedef {import('./plottr').Bookmarks} Bookmarks
 * @typedef {import('./plottr').BookmarkItem} BookmarkItem
 * @typedef {import('./plottr').BookmarkEntityKind} BookmarkEntityKind
 * @typedef {import('./plottr').IdeaBoard} IdeaBoard
 * @typedef {import('./plottr').IdeaItem} IdeaItem
 * @typedef {import('./plottr').IdeaModal} IdeaModal
 * @typedef {import('./plottr').IdeaConfirmModal} IdeaConfirmModal
 * @typedef {import('./plottr').IdeaBaseData} IdeaBaseData
 * @typedef {import('./plottr').PlottrThemes} PlottrThemes
 * @typedef {import('./plottr').AppliedCharacterTemplate} AppliedCharacterTemplate
 * @typedef {import('./plottr').AttributeValue} AttributeValue
 * @typedef {import('./plottr').Slate} Slate
 * @typedef {import('./plottr').SlateRootElement} SlateRootElement
 * @typedef {import('./plottr').Paragraph} Paragraph
 * @typedef {import('./plottr').BlockQuote} BlockQuote
 * @typedef {import('./plottr').HeadingOne} HeadingOne
 * @typedef {import('./plottr').HeadingTwo} HeadingTwo
 * @typedef {import('./plottr').BulletedList} BulletedList
 * @typedef {import('./plottr').NumberedList} NumberedList
 * @typedef {import('./plottr').SlateImageLink} SlateImageLink
 * @typedef {import('./plottr').SlateImageData} SlateImageData
 * @typedef {import('./plottr').TextOrLink} TextOrLink
 * @typedef {import('./plottr').TextChild} TextChild
 * @typedef {import('./plottr').BlockQuoteChild} BlockQuoteChild
 * @typedef {import('./plottr').ListChild} ListChild
 * @typedef {import('./plottr').ListItem} ListItem
 * @typedef {import('./plottr').Attribute} Attribute
 * @typedef {import('./plottr').AudioClipRecord} AudioClipRecord
 * @typedef {import('./plottr').FamilyTree} FamilyTree
 * @typedef {import('./plottr').Member} Member
 * @typedef {import('./plottr').Translation} Translation
 * @typedef {import('./plottr').RelationshipConfiguration} RelationshipConfiguration
 * @typedef {import('./plottr').Relationship} Relationship
 * @typedef {import('./plottr').SingleRelationshipConfiguration} SingleRelationshipConfiguration
 * @typedef {import('./plottr').ShareRecord} ShareRecord
 * @typedef {import('./plottr').FilePermission} FilePermission
 * @typedef {import('./plottr').MobileControlsModal} MobileControlsModal
 * @typedef {import('./plottr').TextAttributeData} TextAttributeData
 * @typedef {import('./plottr-in-your-pocket').UniversalIdeaId} UniversalIdeaId
 */

/**
 * @param {unknown} o
 * @return {TextChild | Null}
 */
const validTextChildOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'text' in o &&
      typeof o.text === 'string' &&
      (('bold' in o && typeof o.bold === 'boolean') || !('bold' in o)) &&
      (('italic' in o && typeof o.italic === 'boolean') || !('italic' in o)) &&
      (('underline' in o && typeof o.underline === 'boolean') || !('underline' in o)) &&
      (('strike' in o && typeof o.strike === 'boolean') || !('strike' in o)) &&
      (('color' in o && typeof o.color === 'string') || !('color' in o)) &&
      (('font' in o && typeof o.font === 'string') || !('font' in o)) &&
      (('fontSize' in o && typeof o.fontSize === 'number') || !('fontSize' in o))
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        text: o.text,
        ...('bold' in o && typeof o.bold === 'boolean' ? { bold: o.bold } : {}),
        ...('italic' in o && typeof o.italic === 'boolean' ? { italic: o.italic } : {}),
        ...('underline' in o && typeof o.underline === 'boolean' ? { underline: o.underline } : {}),
        ...('strike' in o && typeof o.strike === 'boolean' ? { strike: o.strike } : {}),
        ...('color' in o && typeof o.color === 'string' ? { color: o.color } : {}),
        ...('font' in o && typeof o.font === 'string' ? { font: o.font } : {}),
        ...('fontSize' in o && typeof o.fontSize === 'number' ? { fontSize: o.fontSize } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<TextChild> | Null}
 */
const validTextChildrenOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<TextChild> | Null} acc
     * @param {unknown} next
     * @return {Array<TextChild> | Null}
     */
    const accumulateValidTextChildrenOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validTextChild = validTextChildOrNull(next)
        if (validTextChild) {
          return [...acc, validTextChild]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidTextChildrenOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {TextOrLink | Null}
 */
const validTextOrLinkOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('type' in o && o.type === 'link') {
      const children = 'children' in o ? validTextChildrenOrNull(o.children) : null
      if (
        (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
        'url' in o &&
        typeof o.url === 'string' &&
        children
      ) {
        return {
          ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
          type: 'link',
          url: o.url,
          children,
        }
      } else {
        return null
      }
    } else {
      const validTextChild = validTextChildOrNull(o)
      if (validTextChild) {
        return validTextChild
      } else {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<TextOrLink> | Null}
 */
const validTextOrLinkArrayOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<TextOrLink> | Null} acc
     * @param {unknown} next
     * @return {Array<TextOrLink> | Null}
     */
    const accumulateTextOrLinkOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validTextOrLink = validTextOrLinkOrNull(next)
        if (validTextOrLink) {
          return [...acc, validTextOrLink]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateTextOrLinkOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Paragraph | Null}
 */
const validParagraphOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validTextOrLinkArrayOrNull(o.children) : null

    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'paragraph' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'paragraph',
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {BlockQuoteChild | Null}
 */
const validBlockQuoteChildOrNull = (o) => {
  if (o && typeof o === 'object' && 'type' in o) {
    switch (o.type) {
      case 'paragraph': {
        return validParagraphOrNull(o)
      }
      case 'heading-one': {
        return validHeadingOneOrNull(o)
      }
      case 'heading-two': {
        return validHeadingTwoOrNull(o)
      }
      case 'bulleted-list': {
        return validBulletedListOrNull(o)
      }
      case 'numbered-list': {
        return validNumberedListOrNull(o)
      }
      case 'image-link': {
        return validImageLinkOrNull(o)
      }
      case 'image-data': {
        return validImageDataOrNull(o)
      }
      default: {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<BlockQuoteChild> | Null}
 */
const validBlockQuoteChildrenOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<BlockQuoteChild> | Null} acc
     * @param {unknown} next
     * @return {Array<BlockQuoteChild> | Null}
     */
    const accumulateValidBlockQuoteChildrenOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validBlockQuoteChild = validBlockQuoteChildOrNull(next)
        if (validBlockQuoteChild) {
          return [...acc, validBlockQuoteChild]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidBlockQuoteChildrenOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {BlockQuote | Null}
 */
const validBlockQuoteOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validBlockQuoteChildrenOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'block-quote' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'block-quote',
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {HeadingOne | Null}
 */
const validHeadingOneOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validTextChildrenOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'heading-one' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: o.type,
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {HeadingTwo | Null}
 */
const validHeadingTwoOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validTextChildrenOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'heading-two' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: o.type,
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ListItem | Null}
 */
const validListItemOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validTextOrLinkArrayOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'list-item' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'list-item',
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ListChild | Null}
 */
const validListChildOrNull = (o) => {
  if (o && typeof o === 'object' && 'type' in o) {
    switch (o.type) {
      case 'list-item': {
        return validListItemOrNull(o)
      }
      case 'bulleted-list': {
        return validBulletedListOrNull(o)
      }
      case 'numbered-list': {
        return validNumberedListOrNull(o)
      }
      default: {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<ListChild> | Null}
 */
const validListChildrenOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<ListChild> | Null} acc
     * @param {unknown} next
     * @retun {Array<ListChild> | Null}
     */
    const accumulateValidListChildrenOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validListChild = validListChildOrNull(next)
        if (validListChild) {
          return [...acc, validListChild]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidListChildrenOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {BulletedList | Null}
 */
const validBulletedListOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validListChildrenOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'bulleted-list' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'bulleted-list',
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {NumberedList | Null}
 */
const validNumberedListOrNull = (o) => {
  if (o && typeof o === 'object') {
    const children = 'children' in o ? validListChildrenOrNull(o.children) : null
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'numbered-list' &&
      children
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'numbered-list',
        children,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SlateImageLink | Null}
 */
const validImageLinkOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'image-link' &&
      'storageUrl' in o &&
      typeof o.storageUrl === 'string'
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'image-link',
        storageUrl: o.storageUrl,
        children: [],
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SlateImageData | Null}
 */
const validImageDataOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      'type' in o &&
      o.type === 'image-data' &&
      'data' in o &&
      typeof o.data === 'string'
    ) {
      return {
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        type: 'image-data',
        data: o.data,
        children: [],
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SlateRootElement | Null}
 */
const validRootSlateOrNull = (o) => {
  if (o && typeof o === 'object' && 'type' in o) {
    switch (o.type) {
      case 'paragraph': {
        return validParagraphOrNull(o)
      }
      case 'block-quote': {
        return validBlockQuoteOrNull(o)
      }
      case 'heading-one': {
        return validHeadingOneOrNull(o)
      }
      case 'heading-two': {
        return validHeadingTwoOrNull(o)
      }
      case 'bulleted-list': {
        return validBulletedListOrNull(o)
      }
      case 'numbered-list': {
        return validNumberedListOrNull(o)
      }
      case 'image-link': {
        return validImageLinkOrNull(o)
      }
      case 'image-data': {
        return validImageDataOrNull(o)
      }
      default: {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Slate | Null}
 */
const validSlateOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Slate | Null} acc
     * @param {unknown} next
     * @return {Slate | Null}
     */
    const accumulateValidRootSlateOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validRootSlate = validRootSlateOrNull(next)
        if (validRootSlate) {
          return [...acc, validRootSlate]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidRootSlateOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} card
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {PlottrFileCard | null}
 */
const validCardOrNull = (card, customAttributes = []) => {
  if (card && typeof card === 'object') {
    const bookId =
      'bookId' in card &&
      (typeof card.bookId === 'number' || card.bookId === 'series' || card.bookId === null)
        ? card.bookId
        : undefined
    const positionWithinLine =
      'positionWithinLine' in card && typeof card.positionWithinLine === 'number'
        ? card.positionWithinLine
        : undefined
    const positionInBeat =
      'positionInBeat' in card &&
      (typeof card.positionInBeat === 'number' || card.positionInBeat === null)
        ? card.positionInBeat
        : undefined
    const title = 'title' in card && typeof card.title === 'string' ? card.title : undefined
    const description = 'description' in card ? validSlateOrStringOrNull(card.description) : null
    const tags =
      'tags' in card && Array.isArray(card.tags) && card.tags.every((x) => typeof x === 'number')
        ? card.tags
        : undefined
    const characters =
      'characters' in card &&
      Array.isArray(card.characters) &&
      card.characters.every((x) => typeof x === 'number')
        ? card.characters
        : undefined
    const places =
      'places' in card &&
      Array.isArray(card.places) &&
      card.places.every((x) => typeof x === 'number')
        ? card.places
        : undefined
    const templates =
      'templates' in card && Array.isArray(card.templates) ? card.templates : undefined
    const imageId =
      'imageId' in card && (typeof card.imageId === 'number' || card.imageId === null)
        ? card.imageId
        : undefined
    const audioClipId =
      'audioClipId' in card && (typeof card.audioClipId === 'number' || card.audioClipId === null)
        ? card.audioClipId
        : undefined
    const fromTemplateId =
      'fromTemplateId' in card &&
      (typeof card.fromTemplateId === 'string' || card.fromTemplateId === null)
        ? card.fromTemplateId
        : undefined
    const color =
      'color' in card && (typeof card.color === 'string' || card.color === null)
        ? card.color
        : undefined
    const notes =
      'notes' in card && Array.isArray(card.notes) && card.notes.every((x) => typeof x === 'number')
        ? card.notes
        : undefined
    const customAttributesObject = validCustomAttributesOnEntityOrNull(card, customAttributes)
    if (
      'id' in card &&
      typeof card.id === 'number' &&
      'lineId' in card &&
      typeof card.lineId === 'number' &&
      'beatId' in card &&
      typeof card.beatId === 'number' &&
      bookId !== undefined &&
      positionWithinLine !== undefined &&
      positionInBeat !== undefined &&
      title !== undefined &&
      description !== null &&
      tags !== undefined &&
      characters !== undefined &&
      places !== undefined &&
      templates !== undefined &&
      imageId !== undefined &&
      audioClipId !== undefined &&
      fromTemplateId !== undefined &&
      color !== undefined &&
      notes !== undefined &&
      customAttributesObject !== null
    ) {
      return {
        ...customAttributesObject,
        id: card.id,
        lineId: card.lineId,
        beatId: card.beatId,
        bookId,
        positionWithinLine,
        positionInBeat,
        title,
        description,
        tags,
        characters,
        places,
        templates,
        imageId,
        audioClipId,
        fromTemplateId,
        color,
        notes,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} cards
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {Array<PlottrFileCard> | Null}
 */
export const validCardsOrNull = (cards, customAttributes = []) => {
  if (Array.isArray(cards)) {
    const validCards = cards.reduce((acc, next) => {
      const validCard = validCardOrNull(next, customAttributes)
      if (validCard) {
        return [...acc, validCard]
      } else {
        return acc
      }
    }, [])
    if (validCards.length === cards.length) {
      return validCards
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {AppliedCharacterTemplate | Null}
 */
const validAppliedCharacterTemplateOrNull = (o) => {
  if (o && typeof o === 'object') {
    // NOTE(ed): we're re-using the logic for checking a full template
    // and adding dummy fields to avoid duplicated code.
    const validBuiltInTemplate = validCharactersTemplateOrNull({
      ...o,
      type: 'characters',
      description: '',
    })
    const validCustomTemplate = validCustomTemplateOrNull({
      ...o,
      type: 'custom-characters',
      description: '',
    })
    if (validBuiltInTemplate || validCustomTemplate) {
      /**
       * @type {AppliedCharacterTemplate}
       */
      const appliedTemplate = {
        ...omit(validBuiltInTemplate || validCustomTemplate, 'type', 'description'),
        values: [],
        attributes: [],
      }
      return appliedTemplate
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<AppliedCharacterTemplate> | Null}
 */
const validAppliedCharacterTemplatesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<AppliedCharacterTemplate> | Null} acc
     * @param {unknown} next
     * @return {Array<AppliedCharacterTemplate> | Null}
     */
    const accumulateValidAppliedCharacterTemplatesOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validAppliedTemplate = validAppliedCharacterTemplateOrNull(next)
        if (validAppliedTemplate) {
          return [...acc, validAppliedTemplate]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidAppliedCharacterTemplatesOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {AttributeValue | Null}
 */
const validAttributeValueOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @type {Slate | null}
     */
    const validSlate = 'value' in o ? validSlateOrNull(o.value) : null
    const validNonSlateValue =
      'value' in o &&
      (typeof o.value === 'string' ||
        (Array.isArray(o.value) && o.value.every((x) => typeof x === 'number')) ||
        typeof o.value === 'number' ||
        o.value === null)
        ? o.value
        : undefined
    /**
     * @type {Slate | String | Array<number> | number | Null | undefined}
     */
    const value = validSlate ? validSlate : validNonSlateValue
    if (
      'bookId' in o &&
      (typeof o.bookId === 'number' || o.bookId === 'all') &&
      'id' in o &&
      typeof o.id === 'number'
    ) {
      return {
        bookId: o.bookId,
        id: o.id,
        ...('value' in o ? { value } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<AttributeValue> | Null}
 */
const validAttributeValuesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<AttributeValue> | Null} acc
     * @param {unknown} next
     * @return {Array<AttributeValue> | Null}
     */
    const accumulateValidAttributeValuesOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validAttributeValue = validAttributeValueOrNull(next)
        if (validAttributeValue) {
          return [...acc, validAttributeValue]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidAttributeValuesOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Slate | String | null}
 */
const validSlateOrStringOrNull = (o) => {
  if (typeof o === 'string') {
    return o
  } else {
    return validSlateOrNull(o)
  }
}

/**
 * @param {unknown} o
 * @return {CharactersPlottrFileEntry | Null}
 */
const validCharacterOrNull = (o) => {
  if (o && typeof o === 'object') {
    const notes = 'notes' in o ? validSlateOrStringOrNull(o.notes) : null
    const templates = 'templates' in o ? validAppliedCharacterTemplatesOrNull(o.templates) : null
    const attributes = 'attributes' in o ? validAttributeValuesOrNull(o.attributes) : null
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'name' in o &&
      typeof o.name === 'string' &&
      'description' in o &&
      typeof o.description === 'string' &&
      notes !== null &&
      'color' in o &&
      o.color === null &&
      'cards' in o &&
      Array.isArray(o.cards) &&
      o.cards.every((c) => typeof c === 'number') &&
      'noteIds' in o &&
      Array.isArray(o.noteIds) &&
      o.noteIds.every((n) => typeof n === 'number') &&
      templates &&
      'tags' in o &&
      Array.isArray(o.tags) &&
      o.tags.every((t) => typeof t === 'number') &&
      'categoryId' in o &&
      (typeof o.categoryId === 'string' || o.categoryId === null) &&
      'imageId' in o &&
      (typeof o.imageId === 'string' || o.imageId === null) &&
      'audioClipId' in o &&
      (typeof o.audioClipId === 'number' || o.audioClipId === null) &&
      'bookIds' in o &&
      Array.isArray(o.bookIds) &&
      o.bookIds.every((b) => typeof b === 'number' || b === 'series') &&
      'position' in o &&
      typeof o.position === 'number' &&
      attributes
    ) {
      return {
        id: o.id,
        name: o.name,
        description: o.description,
        notes,
        color: o.color,
        cards: o.cards,
        noteIds: o.noteIds,
        templates,
        tags: o.tags,
        categoryId: o.categoryId,
        imageId: o.imageId,
        audioClipId: o.audioClipId,
        bookIds: o.bookIds,
        position: o.position,
        attributes,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Array<CharactersPlottrFileEntry> | Null}
 */
export const validCharactersOrNull = (object) => {
  if (object && Array.isArray(object)) {
    /**
     * @param {Array<CharactersPlottrFileEntry> | Null} acc
     * @param {unknown} next
     * @return {Array<CharactersPlottrFileEntry> | Null}
     */
    const accumulateValidCharactersOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validCharacter = validCharacterOrNull(next)
        if (validCharacter) {
          return [...acc, validCharacter]
        } else {
          return null
        }
      }
    }
    return object.reduce(accumulateValidCharactersOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} beat
 * @return {Beat | null}
 */
const validBeatOrNull = (beat) => {
  if (beat && typeof beat === 'object') {
    const title = 'title' in beat && typeof beat.title === 'string' ? beat.title : undefined
    const time = 'time' in beat && typeof beat.time === 'number' ? beat.time : undefined
    const templates =
      'templates' in beat &&
      Array.isArray(beat.templates) &&
      beat.templates.every((x) => typeof x === 'string')
        ? beat.templates
        : !('templates' in beat)
          ? undefined
          : null
    const autoOutlineSort =
      'autoOutlineSort' in beat && typeof beat.autoOutlineSort === 'boolean'
        ? beat.autoOutlineSort
        : undefined
    const fromTemplateId =
      'fromTemplateId' in beat &&
      (typeof beat.fromTemplateId === 'string' || beat.fromTemplateId === null)
        ? beat.fromTemplateId
        : undefined
    const expanded =
      'expanded' in beat && typeof beat.expanded === 'boolean' ? beat.expanded : undefined
    if (
      'id' in beat &&
      typeof beat.id === 'number' &&
      'bookId' in beat &&
      (typeof beat.bookId === 'number' || beat.bookId === 'series') &&
      'position' in beat &&
      typeof beat.position === 'number' &&
      title !== undefined &&
      time !== undefined &&
      templates !== null &&
      autoOutlineSort !== undefined &&
      fromTemplateId !== undefined &&
      expanded !== undefined
    ) {
      return {
        id: beat.id,
        bookId: beat.bookId,
        position: beat.position,
        title,
        time,
        ...(templates === undefined ? {} : { templates }),
        autoOutlineSort,
        fromTemplateId,
        expanded,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} beatTree
 * @return {PlottrFileBeatsEntry | Null}
 */
const validBeatsEntryOrNull = (beatTree) => {
  if (
    beatTree &&
    typeof beatTree === 'object' &&
    'children' in beatTree &&
    typeof beatTree.children === 'object' &&
    beatTree.children &&
    'heap' in beatTree &&
    beatTree.heap &&
    'index' in beatTree &&
    beatTree.index
  ) {
    /**
     * @param {PlottrFileBeatsEntry['children'] | Null} acc
     * @param {[String, unknown]} next
     * @return {PlottrFileBeatsEntry['children'] | Null}
     */
    const accumulateValidChildrenOrNull = (acc, next) => {
      const [key, xs] = next
      if (acc === null) {
        return null
      } else if (Array.isArray(xs) && xs.every((x) => typeof x === 'number')) {
        return {
          ...acc,
          [key]: xs,
        }
      } else {
        return acc
      }
    }
    const children = Object.entries(beatTree.children).reduce(accumulateValidChildrenOrNull, {})
    /**
     * @param {PlottrFileBeatsEntry['heap'] | Null} acc
     * @param {[String, unknown]} next
     * @return {PlottrFileBeatsEntry['heap'] | Null}
     */
    const accumulateValidHeapEntriesOrNull = (acc, next) => {
      const [key, x] = next
      if (acc === null) {
        return null
      } else if (typeof x === 'number' || x === null) {
        return {
          ...acc,
          [key]: x,
        }
      } else {
        return null
      }
    }

    const heap = Object.entries(beatTree.heap).reduce(accumulateValidHeapEntriesOrNull, {})
    const index = Object.values(beatTree.index).reduce((acc, next) => {
      const validBeat = validBeatOrNull(next)
      if (acc === null) {
        return null
      } else if (validBeat) {
        return {
          ...acc,
          [validBeat.id]: validBeat,
        }
      } else {
        return null
      }
    }, {})
    if (index && heap && children) {
      return {
        children,
        heap,
        index,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {String} x
 * @return {boolean}
 */
const isIntString = (x) => {
  try {
    const res = parseInt(x)
    return `${res}` === x
  } catch (_e) {
    return false
  }
}

/**
 * @param {unknown} beats
 * @return {PlottrFileBeats | Null}
 */
export const validBeatsOrNull = (beats) => {
  if (beats) {
    /**
     * @param {PlottrFileBeats | Null} acc
     * @param {[String, unknown]} entry
     * @return {PlottrFileBeats | Null}
     */
    const accumulateValidBeatsOrNull = (acc, entry) => {
      if (acc === null) {
        return null
      } else {
        const [bookId, beatTree] = entry
        if (bookId && (bookId === 'series' || isIntString(bookId))) {
          const validBeatTree = validBeatsEntryOrNull(beatTree)
          if (validBeatTree) {
            return {
              ...acc,
              [bookId]: validBeatTree,
            }
          } else {
            return null
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(beats).reduce(accumulateValidBeatsOrNull, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} line
 * @return {Line | Null}
 */
const validLineOrNull = (line) => {
  if (line && typeof line === 'object') {
    const color = 'color' in line && typeof line.color === 'string' ? line.color : undefined
    const characterId =
      'characterId' in line && (typeof line.characterId === 'number' || line.characterId === null)
        ? line.characterId
        : undefined
    const title = 'title' in line && typeof line.title === 'string' ? line.title : ''
    const position =
      'position' in line && typeof line.position === 'number' ? line.position : undefined
    const expanded =
      'expanded' in line && (typeof line.expanded === 'boolean' || line.expanded === null)
        ? line.expanded
        : undefined
    const fromTemplateId =
      'fromTemplateId' in line &&
      (typeof line.fromTemplateId === 'string' || line.fromTemplateId === null)
        ? line.fromTemplateId
        : undefined
    const isPinned =
      'isPinned' in line && typeof line.isPinned === 'boolean' ? line.isPinned : undefined
    const pinnedPosition =
      'pinnedPosition' in line &&
      (typeof line.pinnedPosition === 'number' || line.pinnedPosition === null)
        ? line.pinnedPosition
        : undefined
    if (
      'id' in line &&
      typeof line.id === 'number' &&
      'bookId' in line &&
      (typeof line.bookId === 'number' || line.bookId === 'series') &&
      color !== undefined &&
      characterId !== undefined &&
      title !== undefined &&
      position !== undefined &&
      expanded !== undefined &&
      fromTemplateId !== undefined &&
      isPinned !== undefined &&
      pinnedPosition !== undefined
    ) {
      return {
        id: line.id,
        bookId: line.bookId,
        color,
        characterId,
        title,
        position,
        expanded,
        fromTemplateId,
        isPinned,
        pinnedPosition,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} lines
 * @return {Array<Line> | null}
 */
export const validLinesOrNull = (lines) => {
  if (Array.isArray(lines)) {
    const validLines = lines.reduce((acc, nextLine) => {
      const validLine = validLineOrNull(nextLine)
      if (validLine) {
        return [...acc, validLine]
      } else {
        return acc
      }
    }, [])
    if (validLines.length === lines.length) {
      return validLines
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Attribute | Null}
 */
const validAttributeOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'type' in o &&
      (o.type === 'base-attribute' ||
        o.type === 'paragraph' ||
        o.type === 'text' ||
        o.type === 'audio') &&
      'id' in o &&
      typeof o.id === 'number' &&
      'name' in o &&
      typeof o.name === 'string'
    ) {
      return {
        type: o.type,
        id: o.id,
        name: o.name,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Attribute> | Null}
 */
const validAttributesArrayOrNull = (o) => {
  if (Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validAttribute = validAttributeOrNull(next)
        if (validAttribute) {
          return [...acc, validAttribute]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Attributes | Null}
 */
export const validAttributesOrNull = (o) => {
  if (o && typeof o === 'object') {
    const validCharacterAttributes =
      'characters' in o ? validAttributesArrayOrNull(o.characters) : null
    if (validCharacterAttributes) {
      return {
        characters: validCharacterAttributes,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} hierarchyColors
 * @return {ThemeHierarchyColors | Null}
 */
const validHierarchyDarkColorsOrNull = (hierarchyColors) => {
  if (hierarchyColors && typeof hierarchyColors === 'object') {
    const defaultHierarchyLevel = createDefaultHierarchyLevel()
    const borderColor =
      'borderColor' in hierarchyColors && typeof hierarchyColors.borderColor === 'string'
        ? hierarchyColors.borderColor
        : defaultHierarchyLevel.dark.borderColor
    const textColor =
      'textColor' in hierarchyColors && typeof hierarchyColors.textColor === 'string'
        ? hierarchyColors.textColor
        : defaultHierarchyLevel.dark.textColor

    return {
      borderColor,
      textColor,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} hierarchyColors
 * @return {ThemeHierarchyColors | Null}
 */
const validHierarchyLightColorsOrNull = (hierarchyColors) => {
  if (hierarchyColors && typeof hierarchyColors === 'object') {
    const defaultHierarchyLevel = createDefaultHierarchyLevel()
    const borderColor =
      'borderColor' in hierarchyColors && typeof hierarchyColors.borderColor === 'string'
        ? hierarchyColors.borderColor
        : defaultHierarchyLevel.light.borderColor
    const textColor =
      'textColor' in hierarchyColors && typeof hierarchyColors.textColor === 'string'
        ? hierarchyColors.textColor
        : defaultHierarchyLevel.light.textColor

    return {
      borderColor,
      textColor,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} hierarchyLevel
 * @return {HierarchyLevel | Null}
 */
const validHierarchyLevelOrNull = (hierarchyLevel) => {
  if (hierarchyLevel && typeof hierarchyLevel === 'object') {
    const name =
      'name' in hierarchyLevel && typeof hierarchyLevel.name === 'string'
        ? hierarchyLevel.name
        : undefined
    const autoNumber =
      'autoNumber' in hierarchyLevel && typeof hierarchyLevel.autoNumber === 'boolean'
        ? hierarchyLevel.autoNumber
        : undefined
    const borderStyle =
      'borderStyle' in hierarchyLevel &&
      (hierarchyLevel.borderStyle === 'NONE' ||
        hierarchyLevel.borderStyle === 'DASHED' ||
        hierarchyLevel.borderStyle === 'SOLID' ||
        hierarchyLevel.borderStyle === 'DOTTED')
        ? hierarchyLevel.borderStyle
        : undefined
    const backgroundColor =
      'backgroundColor' in hierarchyLevel &&
      (hierarchyLevel.backgroundColor === 'none' ||
        typeof hierarchyLevel.backgroundColor === 'string')
        ? hierarchyLevel.backgroundColor
        : undefined
    const textColor =
      'textColor' in hierarchyLevel && typeof hierarchyLevel.textColor === 'string'
        ? hierarchyLevel.textColor
        : undefined
    const borderColor =
      'borderColor' in hierarchyLevel && typeof hierarchyLevel.borderColor === 'string'
        ? hierarchyLevel.borderColor
        : undefined
    const dark =
      'dark' in hierarchyLevel && typeof hierarchyLevel.dark === 'object' && hierarchyLevel.dark
        ? validHierarchyDarkColorsOrNull(hierarchyLevel.dark)
        : undefined
    const light =
      'light' in hierarchyLevel && typeof hierarchyLevel.light === 'object' && hierarchyLevel.light
        ? validHierarchyLightColorsOrNull(hierarchyLevel.light)
        : null
    if (
      dark &&
      light &&
      'level' in hierarchyLevel &&
      (hierarchyLevel.level === 0 || hierarchyLevel.level === 1 || hierarchyLevel.level === 2) &&
      [0, 1, 2].includes(hierarchyLevel.level) &&
      'textSize' in hierarchyLevel &&
      typeof hierarchyLevel.textSize === 'number' &&
      name !== undefined &&
      autoNumber !== undefined &&
      borderStyle !== undefined &&
      backgroundColor !== undefined &&
      textColor !== undefined &&
      borderColor !== undefined &&
      dark !== undefined &&
      light !== undefined
    ) {
      return {
        level: hierarchyLevel.level,
        textSize: hierarchyLevel.textSize,
        name,
        autoNumber,
        borderStyle,
        backgroundColor,
        textColor,
        borderColor,
        dark,
        light,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} hierarchyLevels
 * @return {HierarchyLevels | Null}
 */
export const validHierarchyLevelsOrNull = (hierarchyLevels) => {
  if (hierarchyLevels && typeof hierarchyLevels === 'object') {
    /**
     * @param {HierarchyLevels | Null} acc
     * @param {[String, unknown]} entry
     * @return {HierarchyLevels | Null}
     */
    const accumulateValidHierarchyLevlesOrNull = (acc, entry) => {
      const [key, next] = entry
      if (acc === null) {
        return null
      } else {
        const validHierarchyLevel = validHierarchyLevelOrNull(next)
        if (validHierarchyLevel) {
          return {
            ...acc,
            [key]: validHierarchyLevel,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(hierarchyLevels).reduce(accumulateValidHierarchyLevlesOrNull, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} fullFileHierarchyLevels
 * @return {HierarchyLevelsPlottrFile | Null}
 */
export const validFullFileHierarchyLevelsOrNull = (fullFileHierarchyLevels) => {
  if (fullFileHierarchyLevels && typeof fullFileHierarchyLevels === 'object') {
    /**
     * @param {HierarchyLevelsPlottrFile | Null} acc
     * @param {[String, unknown]} entry
     * @return {HierarchyLevelsPlottrFile | Null}
     */
    const accumalateValidFullFileHierarchyLevelsOrNull = (acc, entry) => {
      const [key, next] = entry
      if (acc === null) {
        return null
      } else {
        const validHierarchyLevels = validHierarchyLevelsOrNull(next)
        if (validHierarchyLevels) {
          return {
            ...acc,
            [key]: validHierarchyLevels,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(fullFileHierarchyLevels).reduce(
      accumalateValidFullFileHierarchyLevelsOrNull,
      {}
    )
  } else {
    return null
  }
}

/**
 * @param {unknown} legacyCustomAttribute
 * @return {LegacyCustomAttribute | null}
 */
const validLegacyCustomAttributeOrNull = (legacyCustomAttribute) => {
  if (
    legacyCustomAttribute &&
    typeof legacyCustomAttribute === 'object' &&
    'name' in legacyCustomAttribute &&
    typeof legacyCustomAttribute.name === 'string' &&
    'type' in legacyCustomAttribute &&
    (legacyCustomAttribute.type === 'paragraph' ||
      legacyCustomAttribute.type === 'text' ||
      legacyCustomAttribute.type === 'audio')
  ) {
    return {
      name: legacyCustomAttribute.name,
      type: legacyCustomAttribute.type,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} customAttributes
 * @return {LegacyCustomAttributes | Null}
 */
export const validCustomAttributesOrNull = (customAttributes) => {
  if (
    customAttributes &&
    typeof customAttributes === 'object' &&
    'characters' in customAttributes &&
    Array.isArray(customAttributes.characters) &&
    customAttributes.characters.every(validLegacyCustomAttributeOrNull) &&
    'places' in customAttributes &&
    Array.isArray(customAttributes.places) &&
    customAttributes.places.every(validLegacyCustomAttributeOrNull) &&
    'scenes' in customAttributes &&
    Array.isArray(customAttributes.scenes) &&
    customAttributes.scenes.every(validLegacyCustomAttributeOrNull) &&
    'lines' in customAttributes &&
    Array.isArray(customAttributes.lines) &&
    customAttributes.lines.every(validLegacyCustomAttributeOrNull) &&
    'notes' in customAttributes &&
    Array.isArray(customAttributes.notes) &&
    customAttributes.notes.every(validLegacyCustomAttributeOrNull)
  ) {
    return {
      characters: customAttributes.characters,
      places: customAttributes.places,
      scenes: customAttributes.scenes,
      lines: customAttributes.lines,
      notes: customAttributes.notes,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} tag
 * @return {TagsPlottrFileEntry | Null}
 */
export const validTagOrNull = (tag) => {
  if (tag && typeof tag === 'object' && 'id' in tag && typeof tag.id === 'number') {
    const title = 'title' in tag && typeof tag.title === 'string' ? tag.title : ''
    const color = 'color' in tag && typeof tag.color === 'string' ? tag.color : null
    const categoryId =
      'categoryId' in tag && typeof tag.categoryId === 'number' ? tag.categoryId : null

    return {
      id: tag.id,
      title,
      color,
      categoryId,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} tags
 * @return {Array<TagsPlottrFileEntry> | Null}
 */
export const validTagsOrNull = (tags) => {
  if (Array.isArray(tags)) {
    return tags.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validTag = validTagOrNull(next)
        if (validTag) {
          return [...acc, validTag]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} note
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {NotesPlottrFileEntry | null}
 */
const validNoteOrNull = (note, customAttributes = []) => {
  if (note && typeof note === 'object' && 'id' in note && typeof note.id === 'number') {
    const title = 'title' in note && typeof note.title === 'string' ? note.title : undefined
    const content = 'content' in note ? validSlateOrStringOrNull(note.content) : null
    const categoryId =
      'categoryId' in note && (typeof note.categoryId === 'string' || note.categoryId === null)
        ? note.categoryId
        : undefined
    const tags =
      'tags' in note && Array.isArray(note.tags) && note.tags.every((x) => typeof x === 'number')
        ? note.tags
        : undefined
    const characters =
      'characters' in note &&
      Array.isArray(note.characters) &&
      note.characters.every((x) => typeof x === 'number')
        ? note.characters
        : undefined
    const places =
      'places' in note &&
      Array.isArray(note.places) &&
      note.places.every((x) => typeof x === 'number')
        ? note.places
        : undefined
    const lastEdited =
      'lastEdited' in note && (typeof note.lastEdited === 'number' || note.lastEdited === null)
        ? note.lastEdited
        : undefined
    const templates =
      'templates' in note &&
      Array.isArray(note.templates) &&
      note.templates.every((x) => typeof x === 'string')
        ? note.templates
        : undefined
    const imageId =
      'imageId' in note && (typeof note.imageId === 'string' || note.imageId === null)
        ? note.imageId
        : undefined
    const audioClipId =
      'audioClipId' in note && (typeof note.audioClipId === 'number' || note.audioClipId === null)
        ? note.audioClipId
        : undefined
    const bookIds = 'bookIds' in note && Array.isArray(note.bookIds) ? note.bookIds : undefined
    const position =
      'position' in note && typeof note.position === 'number' ? note.position : undefined
    const customAttributesObject = validCustomAttributesOnEntityOrNull(note, customAttributes)
    if (
      title !== undefined &&
      content !== null &&
      categoryId !== undefined &&
      tags !== undefined &&
      characters !== undefined &&
      places !== undefined &&
      lastEdited !== undefined &&
      templates !== undefined &&
      imageId !== undefined &&
      audioClipId !== undefined &&
      bookIds !== undefined &&
      position !== undefined &&
      customAttributesObject
    ) {
      return {
        ...customAttributesObject,
        id: note.id,
        title,
        content,
        categoryId,
        tags,
        characters,
        places,
        lastEdited,
        templates,
        imageId,
        audioClipId,
        bookIds,
        position,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} notes
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {Array<NotesPlottrFileEntry> | null}
 */
export const validNotesOrNull = (notes, customAttributes = []) => {
  if (Array.isArray(notes)) {
    return notes.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validNote = validNoteOrNull(next, customAttributes)
        if (validNote) {
          return [...acc, validNote]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {Record<String, TextAttributeData> | Null}
 */
const validCustomAttributesOnEntityOrNull = (o, customAttributes) => {
  if (o && typeof o === 'object') {
    return customAttributes.reduce((acc, next) => {
      const validAttributeValue = o[next.name]
        ? next.type === 'paragraph'
          ? validSlateOrNull(o[next.name])
          : next.type === 'text'
            ? typeof o[next.name] === 'string'
            : next.type === 'audio'
              ? typeof o[next.name] === 'number'
              : null
        : undefined
      if (validAttributeValue === undefined) {
        return acc
      } else if (validAttributeValue === null) {
        return null
      } else {
        return {
          ...acc,
          [next.name]: o[next.name],
        }
      }
    }, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {PlacesPlottrFileEntry | Null}
 */
const validPlaceOrNull = (o, customAttributes = []) => {
  if (o && typeof o === 'object') {
    const notes = 'notes' in o ? validSlateOrStringOrNull(o.notes) : null
    const customAttributesObject = validCustomAttributesOnEntityOrNull(o, customAttributes)
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'name' in o &&
      typeof o.name === 'string' &&
      'description' in o &&
      typeof o.description === 'string' &&
      notes !== null &&
      'color' in o &&
      o.color === null &&
      'cards' in o &&
      Array.isArray(o.cards) &&
      o.cards.every((c) => typeof c === 'number') &&
      'noteIds' in o &&
      Array.isArray(o.noteIds) &&
      o.noteIds.every((n) => typeof n === 'number') &&
      'templates' in o &&
      Array.isArray(o.templates) &&
      'tags' in o &&
      Array.isArray(o.tags) &&
      o.tags.every((t) => typeof t === 'number') &&
      'imageId' in o &&
      (typeof o.imageId === 'string' || o.imageId === null) &&
      'audioClipId' in o &&
      (typeof o.audioClipId === 'number' || o.audioClipId === null) &&
      'bookIds' in o &&
      Array.isArray(o.bookIds) &&
      o.bookIds.every((b) => typeof b === 'number' || b === 'series') &&
      'position' in o &&
      typeof o.position === 'number' &&
      'categoryId' in o &&
      (typeof o.categoryId === 'string' || o.categoryId === null) &&
      customAttributesObject
    ) {
      return {
        ...customAttributesObject,
        id: o.id,
        name: o.name,
        description: o.description,
        notes,
        color: o.color,
        cards: o.cards,
        noteIds: o.noteIds,
        templates: o.templates,
        tags: o.tags,
        imageId: o.imageId,
        audioClipId: o.audioClipId,
        bookIds: o.bookIds,
        position: o.position,
        categoryId: o.categoryId,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @param {Array<LegacyCustomAttribute>} customAttributes
 * @return {Array<PlacesPlottrFileEntry> | Null}
 */
export const validPlacesOrNull = (object, customAttributes = []) => {
  if (object && Array.isArray(object)) {
    /**
     * @param {Array<PlacesPlottrFileEntry> | Null} acc
     * @param {unknown} next
     * @return {Array<PlacesPlottrFileEntry> | Null}
     */
    const accumulateValidPlaces = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validPlace = validPlaceOrNull(next, customAttributes)
        if (validPlace) {
          return [...acc, validPlace]
        } else {
          return null
        }
      }
    }
    return object.reduce(accumulateValidPlaces, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} category
 * @return {Category | Null}
 */
const validCategoryOrNull = (category) => {
  if (
    category &&
    typeof category === 'object' &&
    'id' in category &&
    typeof category.id === 'number'
  ) {
    const name = 'name' in category && typeof category.name === 'string' ? category.name : ''
    const position =
      'position' in category && typeof category.position === 'number' ? category.position : 0

    return {
      id: category.id,
      name,
      position,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} categories
 * @return {CategoriesPlottrFile | Null}
 */
export const validCategoriesOrNull = (categories) => {
  if (
    categories &&
    typeof categories === 'object' &&
    'characters' in categories &&
    Array.isArray(categories.characters) &&
    'places' in categories &&
    Array.isArray(categories.places) &&
    'notes' in categories &&
    Array.isArray(categories.notes) &&
    'tags' in categories &&
    Array.isArray(categories.tags)
  ) {
    /**
     * @param {Array<Category> | Null} acc
     * @param {unknown} next
     * @return {Array<Category> | Null}
     */
    const reducer = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validCategory = validCategoryOrNull(next)
        if (validCategory) {
          return [...acc, validCategory]
        } else {
          return null
        }
      }
    }
    const characters = categories.characters.reduce(reducer, [])
    const places = categories.places.reduce(reducer, [])
    const notes = categories.notes.reduce(reducer, [])
    const tags = categories.tags.reduce(reducer, [])

    if (characters && places && notes && tags) {
      return {
        characters,
        places,
        notes,
        tags,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} image
 * @return {ImageRecord | Null}
 */
const validImageOrNull = (image) => {
  if (
    image &&
    typeof image === 'object' &&
    'id' in image &&
    typeof image.id === 'number' &&
    'name' in image &&
    typeof image.name === 'string'
  ) {
    const path = 'path' in image && typeof image.path === 'string' ? image.path : null
    const data = 'data' in image && typeof image.data === 'string' ? image.data : null

    if (path || data) {
      return {
        id: image.id,
        name: image.name,
        path,
        data,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} images
 * @return {PlottrFileImages | Null}
 */
export const validImagesOrNull = (images) => {
  if (images && typeof images === 'object') {
    /**
     * @param {PlottrFileImages | Null} acc
     * @param {[String, unknown]} entry
     * @return {PlottrFileImages | Null}
     */
    const accumulateValidImagesOrNull = (acc, entry) => {
      const [key, next] = entry
      if (acc === null) {
        return null
      } else {
        const validImage = validImageOrNull(next)
        if (validImage) {
          return {
            ...acc,
            [key]: validImage,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(images).reduce(accumulateValidImagesOrNull, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} book
 * @return {Book | Null}
 */
const validBookOrNull = (book) => {
  if (book && typeof book === 'object' && 'id' in book && typeof book.id === 'number') {
    const title = 'title' in book && typeof book.title === 'string' ? book.title : undefined
    const premise = 'premise' in book && typeof book.premise === 'string' ? book.premise : undefined
    const genre = 'genre' in book && typeof book.genre === 'string' ? book.genre : undefined
    const theme = 'theme' in book && typeof book.theme === 'string' ? book.theme : undefined
    const templates =
      'templates' in book &&
      Array.isArray(book.templates) &&
      book.templates.every((x) => typeof x === 'string')
        ? book.templates
        : undefined
    const timelineTemplates =
      'timelineTemplates' in book &&
      Array.isArray(book.timelineTemplates) &&
      book.timelineTemplates.every((x) => typeof x === 'string')
        ? book.timelineTemplates
        : undefined
    const imageId =
      'imageId' in book && (typeof book.imageId === 'string' || book.imageId === null)
        ? book.imageId
        : undefined

    if (
      title !== undefined &&
      premise !== undefined &&
      genre !== undefined &&
      theme !== undefined &&
      templates !== undefined &&
      timelineTemplates !== undefined &&
      imageId !== undefined
    ) {
      return {
        id: book.id,
        title,
        premise,
        genre,
        theme,
        templates,
        timelineTemplates,
        imageId,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} books
 * @return {PlottrFileBooks | Null}
 */
export const validBooksOrNull = (books) => {
  if (
    books &&
    typeof books === 'object' &&
    'allIds' in books &&
    Array.isArray(books.allIds) &&
    books.allIds.every((x) => typeof x === 'number')
  ) {
    /**
     * @param {Omit<PlottrFileBooks, 'allIds'> | Null} acc
     * @param {[String, unknown]} entry
     * @return {Omit<PlottrFileBooks, 'allIds'> | Null}
     */
    const accumulateValidBooksOrNull = (acc, entry) => {
      const [key, next] = entry
      if (key === 'allIds') {
        return acc
      } else if (acc === null) {
        return null
      } else {
        const validBook = validBookOrNull(next)
        if (validBook) {
          return {
            ...acc,
            [key]: validBook,
          }
        } else {
          return null
        }
      }
    }
    /**
     * @type {Array<number>}
     */
    const allIds = books.allIds
    const onlyBooks = Object.entries(books).reduce(accumulateValidBooksOrNull, {})
    if (onlyBooks) {
      // FIXME(ed): I'm not sure how to resolve this type checking
      // error.
      //
      // @ts-ignore
      return {
        ...onlyBooks,
        allIds,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {AudioClipRecord | Null}
 */
const validAudioClipRecordOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'name' in o &&
      typeof o.name === 'string' &&
      'path' in o &&
      typeof o.path === 'string' &&
      'data' in o &&
      typeof o.data === 'string'
    ) {
      return {
        id: o.id,
        name: o.name,
        path: o.path,
        data: o.data,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PlottrFileAudioClips | Null}
 */
export const validAudioClipsOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @param {Record<String, AudioClipRecord>} acc
     * @param {[String, unknown]} next
     * @return {Record<String, AudioClipRecord> | Null}
     */
    const accumulateAudioClipRecords = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const [key, value] = next
        const validAudioClipRecord = validAudioClipRecordOrNull(value)
        if (validAudioClipRecord) {
          return {
            ...acc,
            [key]: validAudioClipRecord,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(o).reduce(accumulateAudioClipRecords, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {{ scale: [number, number], x: number, y: number } | Null}
 */
const validStageOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'scale' in o &&
      Array.isArray(o.scale) &&
      o.scale.every((x) => typeof x === 'number') &&
      o.scale[0] &&
      o.scale[1] &&
      'x' in o &&
      typeof o.x === 'number' &&
      'y' in o &&
      typeof o.y === 'number'
    ) {
      return {
        scale: [o.scale[0], o.scale[1]],
        x: o.x,
        y: o.y,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Relationship | Null}
 */
const validRelationshipOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      (('sharedSources' in o &&
        Array.isArray(o.sharedSources) &&
        o.sharedSources.every((x) => typeof x === 'number')) ||
        !('sharedSources' in o)) &&
      'characterId' in o &&
      typeof o.characterId === 'number' &&
      'relationshipName' in o &&
      typeof o.relationshipName === 'string'
    ) {
      return {
        ...('sharedSources' in o &&
        Array.isArray(o.sharedSources) &&
        o.sharedSources.every((x) => typeof x === 'number')
          ? { sharedSources: o.sharedSources }
          : {}),
        characterId: o.characterId,
        relationshipName: o.relationshipName,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Relationship> | Null}
 */
const validRelationshipsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validRelationship = validRelationshipOrNull(next)
        if (validRelationship) {
          return [...acc, validRelationshipOrNull]
        } else {
          return acc
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Member | Null}
 */
const validMemberOrNull = (o) => {
  if (o && typeof o === 'object') {
    const validRelationships =
      'relationships' in o ? validRelationshipsOrNull(o.relationships) : null
    if (
      'characterId' in o &&
      typeof o.characterId === 'number' &&
      'x' in o &&
      typeof o.x === 'number' &&
      'y' in o &&
      typeof o.y === 'number' &&
      validRelationships
    ) {
      return {
        characterId: o.characterId,
        x: o.x,
        y: o.y,
        relationships: validRelationships,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Record<String, Member> | Null}
 */
const validMembersOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @param {Record<String, Member>} acc
     * @param {[String, unknown]} next
     * @return {Record<String, Member> | Null}
     */
    const accumulateMemberRecords = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const [key, value] = next
        const validMemberRecord = validMemberOrNull(value)
        if (validMemberRecord) {
          return {
            ...acc,
            [key]: validMemberRecord,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(o).reduce(accumulateMemberRecords, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Translation | Null}
 */
const validBackgroundImageTranslationOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('x' in o && typeof o.x === 'number' && 'y' in o && typeof o.y === 'number') {
      return {
        x: o.x,
        y: o.y,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SingleRelationshipConfiguration | Null}
 */
const validSingleRelationshipConfigurationOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'type' in o &&
      (o.type === 'parent' || o.type === 'sibling' || o.type === 'child') &&
      'name' in o &&
      typeof o.name === 'string' &&
      'colour' in o &&
      typeof o.colour === 'string' &&
      'lineStyle' in o &&
      (o.lineStyle === 'NONE' ||
        o.lineStyle === 'DASHED' ||
        o.lineStyle === 'SOLID' ||
        o.lineStyle === 'DOTTED')
    ) {
      return {
        type: o.type,
        name: o.name,
        colour: o.colour,
        lineStyle: o.lineStyle,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {RelationshipConfiguration | Null}
 */
const validRelationshipConfigurationOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @param {Record<String, SingleRelationshipConfiguration>} acc
     * @param {[String, unknown]} next
     * @return {Record<String, SingleRelationshipConfiguration> | Null}
     */
    const accumulateSingleRelationshipConfigurationRecords = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const [key, value] = next
        const validSingleRelationshipConfigurationRecord =
          validSingleRelationshipConfigurationOrNull(value)
        if (validSingleRelationshipConfigurationRecord) {
          return {
            ...acc,
            [key]: validSingleRelationshipConfigurationRecord,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(o).reduce(accumulateSingleRelationshipConfigurationRecords, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {FamilyTree | Null}
 */
const validFamilyTreeOrNull = (o) => {
  if (o && typeof o === 'object') {
    const validStage = 'stage' in o ? validStageOrNull(o.stage) : null
    const validMembers = 'members' in o ? validMembersOrNull(o.members) : null
    const validBackgroundImageTranslation =
      'backgroundImageTranslation' in o
        ? validBackgroundImageTranslationOrNull(o.backgroundImageTranslation)
        : undefined
    const validRelationshipConfiguration =
      'relationshipConfiguration' in o
        ? validRelationshipConfigurationOrNull(o.relationshipConfiguration)
        : null
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'name' in o &&
      typeof o.name === 'string' &&
      validStage &&
      validMembers &&
      'backgroundImageId' in o &&
      (typeof o.backgroundImageId === 'string' || o.backgroundImageId === null) &&
      'backgroundColor' in o &&
      (typeof o.backgroundColor === 'string' || o.backgroundColor === null) &&
      validBackgroundImageTranslation !== undefined &&
      validRelationshipConfiguration
    ) {
      return {
        id: o.id,
        name: o.name,
        stage: validStage,
        members: validMembers,
        backgroundImageId: o.backgroundImageId,
        backgroundColor: o.backgroundColor,
        backgroundImageTranslation: validBackgroundImageTranslation,
        relationshipConfiguration: validRelationshipConfiguration,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PlottrFileFamilyTrees | Null}
 */
export const validFamilyTreesOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @param {Record<String, FamilyTree>} acc
     * @param {[String, unknown]} next
     * @return {Record<String, FamilyTree> | Null}
     */
    const accumulateFamilyTreeRecords = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const [key, value] = next
        const validFamilyTreeRecord = validFamilyTreeOrNull(value)
        if (validFamilyTreeRecord) {
          return {
            ...acc,
            [key]: validFamilyTreeRecord,
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(o).reduce(accumulateFamilyTreeRecords, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PlottrFileSeries | Null}
 */
export const validSeriesOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'name' in o &&
      typeof o.name === 'string' &&
      'premise' in o &&
      typeof o.premise === 'string' &&
      'genre' in o &&
      typeof o.genre === 'string' &&
      'theme' in o &&
      typeof o.theme === 'string' &&
      'templates' in o &&
      Array.isArray(o.templates) &&
      o.templates.every((x) => typeof x === 'string')
    ) {
      return {
        name: o.name,
        premise: o.premise,
        genre: o.genre,
        theme: o.theme,
        templates: o.templates,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {PlottrTab | Null}
 */
const validPlottrTabOrNull = (object) => {
  if (
    typeof object === 'string' &&
    (object === 'timeline' ||
      object === 'outline' ||
      object === 'project' ||
      object === 'notes' ||
      object === 'characters' ||
      object === 'places' ||
      object === 'tags')
  ) {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {number | 'series' | Null}
 */
const validTimelineIdOrNull = (object) => {
  if (object === 'series' || typeof object === 'number') {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {TimelineOrientation | Null}
 */
const validTimelineOrientationOrNull = (object) => {
  if (object === 'vertical' || object === 'horizontal') {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} xs
 * @return {Array<number | 'series'> | Null}
 */
const validBookIdArrayOrNull = (xs) => {
  if (Array.isArray(xs)) {
    return xs.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else if (typeof next === 'number' || next === 'series') {
        return [...acc, next]
      } else {
        return null
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {CharacterFilter | Null}
 */
const validCharacterFilterOrNull = (object) => {
  if (object && typeof object === 'object') {
    const bookIdArray = !('book' in object)
      ? undefined
      : 'book' in object
        ? validBookIdArrayOrNull(object.book)
        : null
    const categoryArray = !('category' in object)
      ? undefined
      : 'category' in object &&
          Array.isArray(object.category) &&
          object.category.every((x) => typeof x === 'number')
        ? object.category
        : null
    const tagArray = !('tag' in object)
      ? undefined
      : 'tag' in object &&
          Array.isArray(object.tag) &&
          object.tag.every((x) => typeof x === 'number')
        ? object.tag
        : null
    if (bookIdArray !== null && categoryArray !== null && tagArray !== null) {
      /**
       * @param {CharacterFilter | Null} acc
       * @param {[String, unknown]} next
       * @return {CharacterFilter | Null}
       */
      const accumulateValidCustomAttributeFiltersOrNull = (acc, next) => {
        const [key, value] = next
        if (acc === null) {
          return null
        } else if (key === 'book' || key === 'tag' || key === 'category') {
          return null
        } else if (Array.isArray(value) && value.every((x) => typeof x === 'string')) {
          return {
            ...acc,
            [key]: value,
          }
        } else {
          return null
        }
      }
      // FIXME(ed): I'm not sure how to inform Typescript that we need
      // both generally-named keys for custom attributes and also
      // fields that are fixed and have different types for the base
      // filter attributes.
      //
      // @ts-ignore
      return Object.entries(omit(object, 'book', 'category', 'tag')).reduce(
        accumulateValidCustomAttributeFiltersOrNull,
        {
          ...(bookIdArray ? { book: bookIdArray } : {}),
          ...(categoryArray ? { category: categoryArray } : {}),
          ...(tagArray ? { tag: tagArray } : {}),
        }
      )
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {PlaceFilter | Null}
 */
export const validPlaceFilterOrNull = (object) => {
  if (object && typeof object === 'object') {
    const bookIdArray = !('book' in object)
      ? undefined
      : 'book' in object
        ? validBookIdArrayOrNull(object.book)
        : null
    const tagArray = !('tag' in object)
      ? undefined
      : 'tag' in object &&
          Array.isArray(object.tag) &&
          object.tag.every((x) => typeof x === 'number')
        ? object.tag
        : null
    if (bookIdArray !== null && tagArray !== null) {
      /**
       * @param {PlaceFilter | Null} acc
       * @param {[String, unknown]} next
       * @return {PlaceFilter | Null}
       */
      const accumulateValidCustomAttributeFiltersOrNull = (acc, next) => {
        const [key, value] = next
        if (acc === null) {
          return null
        } else if (key === 'tag' || key === 'book') {
          return null
        } else if (Array.isArray(value) && value.every((x) => typeof x === 'string')) {
          return {
            ...acc,
            [key]: value,
          }
        } else {
          return null
        }
      }
      // FIXME(ed): I'm not sure how to inform Typescript that we need
      // both generally-named keys for custom attributes and also
      // fields that are fixed and have different types for the base
      // filter attributes.
      //
      // @ts-ignore
      return Object.entries(omit(object, 'book', 'tag')).reduce(
        accumulateValidCustomAttributeFiltersOrNull,
        {
          ...(bookIdArray ? { book: bookIdArray } : {}),
          ...(tagArray ? { tag: tagArray } : {}),
        }
      )
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {NoteFilter | Null}
 */
export const validNoteFilterOrNull = (object) => {
  if (object && typeof object === 'object') {
    const bookIdArray = !('book' in object)
      ? undefined
      : 'book' in object
        ? validBookIdArrayOrNull(object.book)
        : null
    const categoryArray = !('category' in object)
      ? undefined
      : 'category' in object &&
          Array.isArray(object.category) &&
          object.category.every((x) => typeof x === 'number')
        ? object.category
        : null
    const tagArray = !('tag' in object)
      ? undefined
      : 'tag' in object &&
          Array.isArray(object.tag) &&
          object.tag.every((x) => typeof x === 'number')
        ? object.tag
        : null
    const characterArray = !('character' in object)
      ? undefined
      : 'character' in object &&
          Array.isArray(object.character) &&
          object.character.every((x) => typeof x === 'number')
        ? object.character
        : null
    const placeArray = !('place' in object)
      ? undefined
      : 'place' in object &&
          Array.isArray(object.place) &&
          object.place.every((x) => typeof x === 'number')
        ? object.place
        : null

    if (
      bookIdArray !== null &&
      tagArray !== null &&
      characterArray !== null &&
      placeArray !== null &&
      categoryArray !== null
    ) {
      /**
       * @param {NoteFilter | Null} acc
       * @param {[String, unknown]} next
       * @return {NoteFilter | Null}
       */
      const accumulateValidCustomAttributeFiltersOrNull = (acc, next) => {
        const [key, value] = next
        if (acc === null) {
          return null
        } else if (
          key === 'tag' ||
          key === 'character' ||
          key === 'place' ||
          key === 'book' ||
          key === 'category'
        ) {
          return null
        } else if (Array.isArray(value) && value.every((x) => typeof x === 'string')) {
          return {
            ...acc,
            [key]: value,
          }
        } else {
          return null
        }
      }
      // FIXME(ed): I'm not sure how to inform Typescript that we need
      // both generally-named keys for custom attributes and also
      // fields that are fixed and have different types for the base
      // filter attributes.
      //
      // @ts-ignore
      return Object.entries(omit(object, 'tag', 'character', 'place', 'book', 'category')).reduce(
        accumulateValidCustomAttributeFiltersOrNull,
        {
          ...(bookIdArray !== null ? { book: bookIdArray } : {}),
          ...(tagArray !== null ? { tag: tagArray } : {}),
          ...(characterArray !== null ? { character: characterArray } : {}),
          ...(placeArray !== null ? { place: placeArray } : {}),
          ...(categoryArray !== null ? { category: categoryArray } : {}),
        }
      )
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {TimelineFilter | Null}
 */
export const validTimelineFilterOrNull = (object) => {
  if (object && typeof object === 'object') {
    const tagArray = !('tag' in object)
      ? undefined
      : 'tag' in object &&
          Array.isArray(object.tag) &&
          object.tag.every((x) => typeof x === 'number')
        ? object.tag
        : null
    const characterArray = !('character' in object)
      ? undefined
      : 'character' in object &&
          Array.isArray(object.character) &&
          object.character.every((x) => typeof x === 'number')
        ? object.character
        : null
    const placeArray = !('place' in object)
      ? undefined
      : 'place' in object &&
          Array.isArray(object.place) &&
          object.place.every((x) => typeof x === 'number')
        ? object.place
        : null
    const colorArray = !('color' in object)
      ? undefined
      : 'color' in object
        ? Array.isArray(object.color) && object.color.every((x) => typeof x === 'string')
        : null

    if (
      tagArray !== null &&
      characterArray !== null &&
      placeArray !== null &&
      colorArray !== null
    ) {
      /**
       * @param {TimelineFilter | Null} acc
       * @param {[String, unknown]} next
       * @return {TimelineFilter | Null}
       */
      const accumulateValidCustomAttributeFiltersOrNull = (acc, next) => {
        const [key, value] = next
        if (acc === null) {
          return null
        } else if (key === 'tag' || key === 'character' || key === 'place' || key === 'color') {
          return null
        } else if (Array.isArray(value) && value.every((x) => typeof x === 'string')) {
          return {
            ...acc,
            [key]: value,
          }
        } else {
          return null
        }
      }
      // FIXME(ed): I'm not sure how to inform Typescript that we need
      // both generally-named keys for custom attributes and also
      // fields that are fixed and have different types for the base
      // filter attributes.
      //
      // @ts-ignore
      return Object.entries(omit(object, 'tag', 'character', 'place', 'color')).reduce(
        accumulateValidCustomAttributeFiltersOrNull,
        {
          ...(tagArray ? { tag: tagArray } : {}),
          ...(characterArray ? { character: characterArray } : {}),
          ...(placeArray ? { place: placeArray } : {}),
          ...(colorArray ? { color: colorArray } : {}),
        }
      )
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {TimelineScrollPosition | Null}
 */
const validTimelineScrollPositionOrNull = (object) => {
  if (
    object &&
    typeof object == 'object' &&
    'x' in object &&
    typeof object.x === 'number' &&
    'y' in object &&
    typeof object.y === 'number'
  ) {
    return {
      x: object.x,
      y: object.y,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {boolean}
 */
const isStringNumber = (object) => {
  if (typeof object === 'string') {
    try {
      const parsed = parseInt(object)
      if (`${parsed}` === object) {
        return true
      } else {
        return false
      }
    } catch (_e) {
      return false
    }
  } else {
    return false
  }
}

/**
 * @param {unknown} object
 * @return {PinnedPlotlines | Null}
 */
const validPinnedPlotlinesOrNull = (object) => {
  if (object && typeof object === 'object') {
    /**
     * @param {PinnedPlotlines | Null} acc
     * @param {[String, unknown]} next
     * @return {PinnedPlotlines | Null}
     */
    const accumulateValidPinnedPlotlinesOrNull = (acc, next) => {
      const [key, value] = next
      if (acc === null) {
        return null
      } else if ((isStringNumber(key) || key === 'series') && typeof value === 'number') {
        return {
          ...acc,
          [key]: value,
        }
      } else {
        return null
      }
    }
    return Object.entries(object).reduce(accumulateValidPinnedPlotlinesOrNull, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {TimelineSize | Null}
 */
const validTimelineSizeOrNull = (object) => {
  if (object === 'small' || object === 'medium' || object === 'large') {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {ActView | Null}
 */
const validTimelineActViewOrNull = (object) => {
  if (object === 'default' || object === 'tabbed' || object === 'stacked') {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {FocusPath | Null}
 */
const validFocusPathOrNull = (object) => {
  if (
    Array.isArray(object) &&
    object.every((x) => typeof x === 'string' || typeof x === 'number' || x === null)
  ) {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Point | Null}
 */
const validPointOrNull = (object) => {
  if (object && typeof object === 'object') {
    if ('path' in object) {
      // NOTE(ed): Testing for RCEPoint
      const path = validFocusPathOrNull(object.path)
      const offset = 'offset' in object && typeof object.offset === 'number' ? object.offset : null
      if (path && offset !== null) {
        return {
          path,
          offset,
        }
      } else {
        return null
      }
    } else if ('offset' in object) {
      // NOTE(ed): Testing for TextBoxPoint
      if (typeof object.offset === 'number') {
        return {
          offset: object.offset,
        }
      } else {
        return null
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Selection | DOMSelection | Null}
 */
const validFocusSelectionOrNull = (object) => {
  if (object && typeof object === 'object') {
    if ('anchor' in object) {
      // NOTE(ed): Testing for Selection
      const anchor = validPointOrNull(object.anchor)
      const focus = 'focus' in object ? validPointOrNull(object.focus) : null
      if (anchor && focus) {
        return {
          anchor,
          focus,
        }
      } else {
        return null
      }
    } else if ('start' in object) {
      // NOTE(ed): Testing for DOMSelection
      if (
        typeof object.start === 'number' &&
        'end' in object &&
        typeof object.end === 'number' &&
        'direction' in object &&
        (object.direction === 'backward' ||
          object.direction === 'forward' ||
          object.direction === 'none')
      ) {
        return {
          start: object.start,
          end: object.end,
          direction: object.direction,
        }
      } else {
        return null
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Focus | Null}
 */
const validFocusOrNull = (object) => {
  if (object && typeof object === 'object') {
    const path = 'path' in object ? validFocusPathOrNull(object.path) : null
    const selection =
      'selection' in object && object.selection !== null
        ? validFocusSelectionOrNull(object.selection) || undefined
        : null
    if (path && selection !== undefined) {
      return {
        path,
        selection,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Array<Focus> | Null}
 */
const validFocusStackOrNull = (object) => {
  if (Array.isArray(object) && object.every(validFocusOrNull)) {
    return object
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {SearchTerms | Null}
 */
const validUISearchTermsOrNull = (object) => {
  if (object && typeof object === 'object') {
    if (
      'notes' in object &&
      (typeof object.notes === 'string' || object.notes === null) &&
      'characters' in object &&
      (typeof object.characters === 'string' || object.characters === null) &&
      'places' in object &&
      (typeof object.places === 'string' || object.places === null) &&
      'tags' in object &&
      (typeof object.tags === 'string' || object.tags === null) &&
      'outline' in object &&
      (typeof object.outline === 'string' || object.outline === null) &&
      'timeline' in object &&
      (typeof object.timeline === 'string' || object.timeline === null) &&
      'ideas' in object &&
      (typeof object.ideas === 'string' || object.ideas === null) &&
      'bookmarks' in object &&
      (typeof object.bookmarks === 'string' || object.bookmarks === null)
    ) {
      return {
        notes: object.notes,
        characters: object.characters,
        places: object.places,
        tags: object.tags,
        outline: object.outline,
        timeline: object.timeline,
        ideas: object.ideas,
        bookmarks: object.bookmarks,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {AttributeTab | Null}
 */
const validUiAttributeTabsOrNull = (object) => {
  if (object && typeof object === 'object') {
    if (
      'characters' in object &&
      (typeof object.characters === 'number' || object.characters === 'all')
    ) {
      return {
        characters: object.characters,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} templateAttribute
 * @return {CharacterTemplateAttribute | SceneTemplateAttribute | Null}
 */
export const validTemplateAttributeOrNull = (templateAttribute) => {
  if (
    templateAttribute &&
    typeof templateAttribute === 'object' &&
    'name' in templateAttribute &&
    typeof templateAttribute.name === 'string' &&
    'type' in templateAttribute &&
    typeof templateAttribute.type === 'string' &&
    (templateAttribute.type === 'text' || templateAttribute.type === 'paragraph') &&
    (('description' in templateAttribute && typeof templateAttribute.description === 'string') ||
      !('description' in templateAttribute)) &&
    (('link' in templateAttribute && typeof templateAttribute.link === 'string') ||
      !('link' in templateAttribute))
  ) {
    return {
      name: templateAttribute.name,
      type: templateAttribute.type,
      ...('description' in templateAttribute && typeof templateAttribute.description === 'string'
        ? { description: templateAttribute.description }
        : { description: '' }),
      ...('link' in templateAttribute && typeof templateAttribute.link === 'string'
        ? { link: templateAttribute.link }
        : {}),
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} templateAttributeArray
 * @return {Array<CharacterTemplateAttribute> | Array<SceneTemplateAttribute> | Null}
 */
export const validTemplateAttributeArrayOrNull = (templateAttributeArray) => {
  if (
    Array.isArray(templateAttributeArray) &&
    templateAttributeArray.every(validTemplateAttributeOrNull)
  ) {
    return templateAttributeArray
  } else {
    return null
  }
}

/**
 * @param {unknown} template
 * @return {CharacterTemplate | Null}
 */
export const validCharactersTemplateOrNull = (template) => {
  if (
    template &&
    typeof template === 'object' &&
    'id' in template &&
    typeof template.id === 'string' &&
    'type' in template &&
    template.type === 'characters' &&
    'name' in template &&
    typeof template.name === 'string' &&
    'description' in template &&
    typeof template.description === 'string' &&
    'version' in template &&
    typeof template.version === 'string' &&
    'attributes' in template &&
    typeof template.attributes === 'object' &&
    template.attributes &&
    Array.isArray(template.attributes)
  ) {
    const attributes = validTemplateAttributeArrayOrNull(template.attributes)
    if (attributes) {
      return {
        id: template.id,
        type: template.type,
        name: template.name,
        description: template.description,
        version: template.version,
        attributes,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} mergeBias
 * @return {MergeBias | null}
 */
const validMergeBiasOrNull = (mergeBias) => {
  if (mergeBias === 'top' || mergeBias === 'middle' || mergeBias === 'bottom') {
    return mergeBias
  } else {
    return null
  }
}

/**
 * @param {unknown} customTemplateData
 * @return {PlotlineCustomTemplateData | Null}
 */
const validCustomPlotlineTemplateDataOrNull = (customTemplateData) => {
  if (customTemplateData && typeof customTemplateData === 'object') {
    const cards =
      'cards' in customTemplateData &&
      typeof customTemplateData.cards === 'object' &&
      customTemplateData.cards
        ? validCardsOrNull(customTemplateData.cards)
        : null
    const beats =
      'beats' in customTemplateData &&
      typeof customTemplateData.beats === 'object' &&
      customTemplateData.beats
        ? validBeatsOrNull(customTemplateData.beats)
        : null
    const lines =
      'lines' in customTemplateData &&
      typeof customTemplateData.lines === 'object' &&
      customTemplateData.lines
        ? validLinesOrNull(customTemplateData.lines)
        : null
    const mergeBias =
      'mergeBias' in customTemplateData ? validMergeBiasOrNull(customTemplateData.mergeBias) : null
    const fullFileHierarchyLevels =
      'hierarchyLevels' in customTemplateData &&
      typeof customTemplateData.hierarchyLevels === 'object' &&
      customTemplateData.hierarchyLevels
        ? validFullFileHierarchyLevelsOrNull(customTemplateData.hierarchyLevels)
        : null
    const singleTimelineHierarchyLevels =
      'hierarchyLevels' in customTemplateData
        ? validHierarchyLevelsOrNull(customTemplateData.hierarchyLevels)
        : null
    if (
      (cards || cards === undefined) &&
      beats &&
      lines &&
      (fullFileHierarchyLevels || singleTimelineHierarchyLevels)
    ) {
      const newFileHierarchyLevels = createNewFileHierarchies()
      return {
        cards,
        beats,
        lines,
        hierarchyLevels: singleTimelineHierarchyLevels
          ? singleTimelineHierarchyLevels
          : fullFileHierarchyLevels
            ? fullFileHierarchyLevels[1]
            : newFileHierarchyLevels[1],
        ...(mergeBias ? { mergeBias } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @typedef {Array<CharacterCustomTemplateAttribute> | Array<SceneCustomTemplateAttribute>} NonPlotlineCustomTemplateData
 * @typedef {CharacterCustomTemplateAttribute | SceneCustomTemplateAttribute} CharacterOrSceneTemplateTemplateAttribute
 */

/**
 * @param {unknown} o
 * @return {NonPlotlineCustomTemplateData | Null}
 */
const validCustomCharacterOrSceneTemplateDataOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<CharacterOrSceneTemplateTemplateAttribute> | Null} acc
     * @param {unknown} next
     * @return {Array<CharacterOrSceneTemplateTemplateAttribute> | Null}
     */
    const accumulateValidCustomTemplateAttributesOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        if (
          next &&
          typeof next === 'object' &&
          'type' in next &&
          (next.type === 'text' || next.type === 'paragraph') &&
          'name' in next &&
          typeof next.name === 'string' &&
          (('description' in next && typeof next.description === 'string') ||
            !('description' in next))
        ) {
          return [
            ...acc,
            {
              type: next.type,
              name: next.name,
              ...('description' in next && typeof next.description === 'string'
                ? { description: next.description }
                : { description: '' }),
            },
          ]
        } else {
          return null
        }
      }
    }
    const validAttributes = o.reduce(accumulateValidCustomTemplateAttributesOrNull, [])
    if (validAttributes) {
      return validAttributes
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {'plotlines'|'custom-characters'|'characters'|'custom-scenes'} type
 * @param {unknown} customTemplateData
 * @return {{ type: 'plotlines', templateData: PlotlineCustomTemplateData } | { type: 'custom-characters'|'characters'|'custom-scenes', templateData: NonPlotlineCustomTemplateData } | Null}
 */
export const validCustomTemplateDataOrNull = (type, customTemplateData) => {
  if (type === 'plotlines' && customTemplateData && typeof customTemplateData === 'object') {
    const validPlotlineCustomData = validCustomPlotlineTemplateDataOrNull(customTemplateData)
    if (validPlotlineCustomData) {
      return { type: 'plotlines', templateData: validPlotlineCustomData }
    } else {
      return null
    }
  } else if (type === 'custom-characters' || type === 'characters' || type === 'custom-scenes') {
    const validNonPlotlineCustomData =
      validCustomCharacterOrSceneTemplateDataOrNull(customTemplateData)
    if (validNonPlotlineCustomData) {
      return { type, templateData: validNonPlotlineCustomData }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} customTemplate
 * @return {CustomTemplate | Null}
 */
export const validCustomTemplateOrNull = (customTemplate) => {
  if (
    customTemplate &&
    typeof customTemplate === 'object' &&
    'id' in customTemplate &&
    typeof customTemplate.id === 'string' &&
    'version' in customTemplate &&
    typeof customTemplate.version === 'string' &&
    'type' in customTemplate &&
    (customTemplate.type === 'plotlines' ||
      customTemplate.type === 'characters' ||
      customTemplate.type === 'custom-characters' ||
      customTemplate.type === 'custom-scenes' ||
      customTemplate.type === 'scenes') &&
    (('name' in customTemplate && typeof customTemplate.name === 'string') ||
      !('name' in customTemplate))
  ) {
    const description =
      'description' in customTemplate && typeof customTemplate.description === 'string'
        ? customTemplate.description
        : ''
    const link =
      'link' in customTemplate && typeof customTemplate.link === 'string' ? customTemplate.link : ''
    const templateType = customTemplate.type === 'scenes' ? 'custom-scenes' : customTemplate.type
    const validCustomTemplateData = validCustomTemplateDataOrNull(
      templateType,
      templateType === 'plotlines'
        ? 'templateData' in customTemplate
          ? customTemplate.templateData
          : null
        : 'attributes' in customTemplate
          ? customTemplate.attributes
          : null
    )
    if (validCustomTemplateData) {
      if (validCustomTemplateData.type === 'plotlines') {
        return {
          id: customTemplate.id,
          version: customTemplate.version,
          ...('name' in customTemplate && typeof customTemplate.name === 'string'
            ? { name: customTemplate.name }
            : {}),
          type: 'plotlines',
          description,
          link,
          templateData: validCustomTemplateData.templateData,
        }
      } else {
        return {
          id: customTemplate.id,
          version: customTemplate.version,
          ...('name' in customTemplate && typeof customTemplate.name === 'string'
            ? { name: customTemplate.name }
            : {}),
          type: validCustomTemplateData.type,
          description,
          link,
          attributes: validCustomTemplateData.templateData,
        }
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {Null | CharacterTemplate | CharacterCustomTemplate}
 */
const validCharacterTemplateDataOrNull = (object) => {
  const characterTemplate = validCharactersTemplateOrNull(object)
  const characterCustomTemplate = validCustomTemplateOrNull(object)
  if (characterTemplate) {
    return characterTemplate
  } else if (
    characterCustomTemplate &&
    (characterCustomTemplate.type === 'characters' ||
      characterCustomTemplate.type === 'custom-characters')
  ) {
    // NOTE(ed): there are applied custom templates in the wild that
    // have no name.
    return {
      ...characterCustomTemplate,
      name: characterCustomTemplate.name ?? translate('Untitled'),
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {CharacterTabCharacterEditor | Null}
 */
const validCharacterEditorOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'deleting' in o &&
      typeof o.deleting === 'boolean' &&
      'removing' in o &&
      typeof o.removing === 'boolean' &&
      'removeWhichTemplate' in o &&
      (typeof o.removeWhichTemplate === 'string' || o.removeWhichTemplate === null) &&
      'activeTab' in o &&
      typeof o.activeTab === 'number' &&
      'showTemplatePicker' in o &&
      typeof o.showTemplatePicker === 'boolean'
    ) {
      return {
        deleting: o.deleting,
        removing: o.removing,
        removeWhichTemplate: o.removeWhichTemplate,
        activeTab: o.activeTab,
        showTemplatePicker: o.showTemplatePicker,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {CharacterTabUi | Null}
 */
const validCharacterTabUiOrNull = (object) => {
  if (object && typeof object === 'object') {
    const focus = 'focus' in object ? validFocusStackOrNull(object.focus) : null
    const templateData =
      'templateData' in object && typeof object.templateData === 'object'
        ? validCharacterTemplateDataOrNull(object.templateData)
        : undefined
    const characterEditor =
      'characterEditor' in object && typeof object.characterEditor === 'object'
        ? validCharacterEditorOrNull(object.characterEditor)
        : null
    if (
      'selectedCharacter' in object &&
      (typeof object.selectedCharacter === 'number' || object.selectedCharacter === null) &&
      focus &&
      'attributesDialogOpen' in object &&
      typeof object.attributesDialogOpen === 'boolean' &&
      'categoriesDialogOpen' in object &&
      typeof object.categoriesDialogOpen === 'boolean' &&
      'editingSelected' in object &&
      typeof object.editingSelected === 'boolean' &&
      'showTemplatePicker' in object &&
      typeof object.showTemplatePicker === 'boolean' &&
      'creating' in object &&
      typeof object.creating === 'boolean' &&
      templateData !== undefined &&
      'filterVisible' in object &&
      typeof object.filterVisible === 'boolean' &&
      'sortVisible' in object &&
      typeof object.sortVisible === 'boolean' &&
      'detailsVisible' in object &&
      typeof object.detailsVisible === 'boolean' &&
      characterEditor &&
      'characterSelectedForChangingImage' in object &&
      (typeof object.characterSelectedForChangingImage === 'number' ||
        object.characterSelectedForChangingImage === null) &&
      'relationshipEditorOpen' in object &&
      typeof object.relationshipEditorOpen === 'boolean' &&
      'view' in object &&
      (object.view === 'default' || object.view === 'tree') &&
      'collapsedCategories' in object &&
      typeof object.collapsedCategories === 'boolean' &&
      'showAsList' in object &&
      typeof object.showAsList === 'boolean'
    ) {
      return {
        selectedCharacter: object.selectedCharacter,
        focus,
        attributesDialogOpen: object.attributesDialogOpen,
        categoriesDialogOpen: object.categoriesDialogOpen,
        editingSelected: object.editingSelected,
        showTemplatePicker: object.showTemplatePicker,
        creating: object.creating,
        templateData,
        filterVisible: object.filterVisible,
        sortVisible: object.sortVisible,
        detailsVisible: object.detailsVisible,
        characterEditor,
        characterSelectedForChangingImage: object.characterSelectedForChangingImage,
        relationshipEditorOpen: object.relationshipEditorOpen,
        view: object.view,
        collapsedCategories: object.collapsedCategories,
        showAsList: object.showAsList,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {CustomAttributeOrderUiPlottrFile | Null}
 */
const validCustomAttributeOrderOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('characters' in o && Array.isArray(o.characters)) {
      /**
       * @param {Array<AttributeOrderEntry> | Null} acc
       * @param {unknown} next
       * @return {Array<AttributeOrderEntry> | Null}
       */
      const accumulateValidCustomAttributeOrderEntryOrNull = (acc, next) => {
        if (acc === null) {
          return null
        } else if (
          next &&
          typeof next === 'object' &&
          'type' in next &&
          next.type === 'attributes' &&
          'id' in next &&
          typeof next.id === 'number'
        ) {
          return [
            ...acc,
            {
              id: next.id,
              type: next.type,
            },
          ]
        } else if (
          next &&
          typeof next === 'object' &&
          'name' in next &&
          typeof next.name === 'string'
        ) {
          return [
            ...acc,
            {
              name: next.name,
            },
          ]
        } else {
          return null
        }
      }
      const validCharactersCustomAttributeOrderOrNull = o.characters.reduce(
        accumulateValidCustomAttributeOrderEntryOrNull,
        []
      )
      if (validCharactersCustomAttributeOrderOrNull) {
        return { characters: validCharactersCustomAttributeOrderOrNull }
      } else {
        return null
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {CardDialogUi | null}
 */
const validCardDialogUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'cardId' in o &&
      (typeof o.cardId === 'number' || o.cardId === null) &&
      'lineId' in o &&
      (typeof o.lineId === 'number' || o.lineId === null) &&
      'beatId' in o &&
      (typeof o.beatId === 'number' || o.beatId === null) &&
      'isOpen' in o &&
      typeof o.isOpen === 'boolean' &&
      'deleting' in o &&
      typeof o.deleting === 'boolean' &&
      'showColorPicker' in o &&
      typeof o.showColorPicker === 'boolean' &&
      'showTemplatePicker' in o &&
      typeof o.showTemplatePicker === 'boolean' &&
      'removing' in o &&
      typeof o.removing === 'boolean' &&
      'removeWhichTemplate' in o &&
      (typeof o.removeWhichTemplate === 'string' || o.removeWhichTemplate === null) &&
      (('activeTab' in o && typeof o.activeTab === 'number') || !('activeTab' in o))
    ) {
      return {
        cardId: o.cardId,
        lineId: o.lineId,
        beatId: o.beatId,
        isOpen: o.isOpen,
        deleting: o.deleting,
        showColorPicker: o.showColorPicker,
        showTemplatePicker: o.showTemplatePicker,
        removing: o.removing,
        removeWhichTemplate: o.removeWhichTemplate,
        ...('activeTab' in o && typeof o.activeTab === 'number' ? { activeTab: o.activeTab } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {UiCollaborators | Null}
 */
const validUiCollaboratorsOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'collaborators' in o &&
      Array.isArray(o.collaborators) &&
      'viewers' in o &&
      Array.isArray(o.viewers)
    ) {
      /**
       * @param {Array<CollaboratorUIState> | Null} acc
       * @param {unknown} next
       * @return {Array<CollaboratorUIState> | Null}
       */
      const accumulateValidCollaboratorsOrNull = (acc, next) => {
        if (acc === null) {
          return null
        } else if (next && typeof next === 'object') {
          const validCollaboratorState = validUiOrNull({
            ...next,
            collaborators: {
              collaborators: [],
              viewers: [],
            },
          })
          if ('id' in next && typeof next.id === 'string' && validCollaboratorState) {
            /**
             * @type {CollaboratorUIState}
             */
            const validCollaboratorStateWithOnlyApplicableFields = {
              ...omit(validCollaboratorState, 'collaborators'),
              id: next.id,
            }
            return [...acc, validCollaboratorStateWithOnlyApplicableFields]
          } else {
            return null
          }
        } else {
          return null
        }
      }

      const validCollaboratorsArrayOrNull = o.collaborators.reduce(
        accumulateValidCollaboratorsOrNull,
        []
      )
      const validViewersArrayOrNull = o.viewers.reduce(accumulateValidCollaboratorsOrNull, [])

      if (validCollaboratorsArrayOrNull && validViewersArrayOrNull) {
        return {
          collaborators: validCollaboratorsArrayOrNull,
          viewers: validViewersArrayOrNull,
        }
      } else {
        return null
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {BookDialog | Null}
 */
const validBookDialogOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'bookId' in o &&
      (typeof o.bookId === 'number' || o.bookId === null || o.bookId === 'series') &&
      'isOpen' in o &&
      typeof o.isOpen === 'boolean'
    ) {
      return {
        bookId: o.bookId,
        isOpen: o.isOpen,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SearchHit | null}
 */
const validSearchHitOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('path' in o && typeof o.path === 'string' && 'hit' in o && typeof o.hit === 'string') {
      return {
        path: o.path,
        hit: o.hit,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<SearchHit> | Null}
 */
const validSearchHitsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<SearchHit> | Null} acc
     * @param {unknown} next
     * @return {Array<SearchHit> | Null}
     */
    const accumulateValidSearchHitsOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validSearchHit = validSearchHitOrNull(next)
        if (validSearchHit) {
          return [...acc, validSearchHit]
        } else {
          return null
        }
      }
    }
    const validSearchHits = o.reduce(accumulateValidSearchHitsOrNull, [])
    if (validSearchHits) {
      return validSearchHits
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SearchDialogActiveTab | Null}
 */
const validSearchDialogActiveTabOrNull = (o) => {
  if (o === 'search' || o === 'replace' || o === 'bookmarks') {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {SearchDialogUi | Null}
 */
const validSearchDialogUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const hitsToReplace =
      'hitsToReplace' in o && Array.isArray(o.hitsToReplace)
        ? validSearchHitsOrNull(o.hitsToReplace)
        : null
    const activeTab = 'activeTab' in o ? validSearchDialogActiveTabOrNull(o.activeTab) : null
    if (
      'isOpen' in o &&
      typeof o.isOpen === 'boolean' &&
      'term' in o &&
      typeof o.term === 'string' &&
      'currentHitIndex' in o &&
      (typeof o.currentHitIndex === 'number' || o.currentHitIndex === null) &&
      'scanning' in o &&
      typeof o.scanning === 'boolean' &&
      'replacing' in o &&
      typeof o.replacing === 'boolean' &&
      'replacement' in o &&
      typeof o.replacement === 'string' &&
      hitsToReplace &&
      'replaceWord' in o &&
      typeof o.replaceWord === 'boolean' &&
      'jumping' in o &&
      typeof o.jumping === 'boolean' &&
      activeTab
    ) {
      return {
        isOpen: o.isOpen,
        term: o.term,
        currentHitIndex: o.currentHitIndex,
        scanning: o.scanning,
        replacing: o.replacing,
        replacement: o.replacement,
        hitsToReplace,
        replaceWord: o.replaceWord,
        jumping: o.jumping,
        activeTab,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ProjectTabUi | Null}
 */
const validProjectTabOrNull = (o) => {
  if (o && typeof o === 'object') {
    const focus = 'focus' in o ? validFocusStackOrNull(o.focus) : null
    if (focus) {
      return {
        focus,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineCardEditor | Null}
 */
const validCardEditorOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('editing' in o && (typeof o.editing === 'number' || o.editing === null)) {
      return {
        editing: o.editing,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineTabView | Null}
 */
const validOutlineTabViewOrNull = (o) => {
  if (o === 'plan' || o === 'fulltext' || o === 'legacy') {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineFulltextHoverDetails | Null}
 */
const validHoverDetails = (o) => {
  if (o && typeof o === 'object') {
    if (
      'cardTitle' in o &&
      (typeof o.cardTitle === 'string' || o.cardTitle === null) &&
      'beatTitle' in o &&
      (typeof o.beatTitle === 'string' || o.beatTitle === null) &&
      'lineTitle' in o &&
      (typeof o.lineTitle === 'string' || o.lineTitle === null) &&
      'cardId' in o &&
      (typeof o.cardId === 'number' || o.cardId === null) &&
      'visible' in o &&
      typeof o.visible === 'boolean'
    ) {
      return {
        cardTitle: o.cardTitle,
        beatTitle: o.beatTitle,
        lineTitle: o.lineTitle,
        cardId: o.cardId,
        visible: o.visible,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineFulltextUiLayout | Null}
 */
const validFulltext = (o) => {
  if (o && typeof o === 'object') {
    const hoverDetails =
      'hoverDetails' in o && typeof o.hoverDetails === 'object'
        ? validHoverDetails(o.hoverDetails)
        : null
    if (
      'showCardTitle' in o &&
      typeof o.showCardTitle === 'boolean' &&
      'showBeatTitle' in o &&
      typeof o.showBeatTitle === 'boolean' &&
      'showLayoutConfig' in o &&
      typeof o.showLayoutConfig === 'boolean' &&
      'showBeatGaps' in o &&
      typeof o.showBeatGaps === 'boolean' &&
      'cardTitleAlignment' in o &&
      (o.cardTitleAlignment === 'left' ||
        o.cardTitleAlignment === 'center' ||
        o.cardTitleAlignment === 'right') &&
      'beatTitleAlignment' in o &&
      (o.beatTitleAlignment === 'left' ||
        o.beatTitleAlignment === 'center' ||
        o.beatTitleAlignment === 'right') &&
      'detailsPinned' in o &&
      typeof o.detailsPinned === 'boolean' &&
      hoverDetails &&
      'zoom' in o &&
      typeof o.zoom === 'number' &&
      'light' in o &&
      typeof o.light === 'string'
    ) {
      return {
        showCardTitle: o.showCardTitle,
        showBeatTitle: o.showBeatTitle,
        showLayoutConfig: o.showLayoutConfig,
        showBeatGaps: o.showBeatGaps,
        cardTitleAlignment: o.cardTitleAlignment,
        beatTitleAlignment: o.beatTitleAlignment,
        detailsPinned: o.detailsPinned,
        hoverDetails,
        zoom: o.zoom,
        light: o.light,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineTabCardUiLayout | Null}
 */
const validCard = (o) => {
  if (o && typeof o === 'object') {
    if ('colorSource' in o && (o.colorSource === 'card' || o.colorSource === 'plotline')) {
      return {
        colorSource: o.colorSource,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineTabUiLayout | Null}
 */
const layoutOrNull = (o) => {
  if (o && typeof o === 'object') {
    const view = 'view' in o ? validOutlineTabViewOrNull(o.view) : null
    const fulltext =
      'fulltext' in o && typeof o.fulltext === 'object' ? validFulltext(o.fulltext) : null
    const card = 'card' in o && typeof o.card === 'object' ? validCard(o.card) : null
    if (view && fulltext && card) {
      return {
        view,
        fulltext,
        card,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {OutlineTabUi | Null}
 */
const validOutlineTabOrNull = (o) => {
  if (o && typeof o === 'object') {
    const focus = 'focus' in o ? validFocusStackOrNull(o.focus) : null
    const cardEditor =
      'cardEditor' in o && typeof o.cardEditor === 'object'
        ? validCardEditorOrNull(o.cardEditor)
        : null
    const layout = 'layout' in o && typeof o.layout === 'object' ? layoutOrNull(o.layout) : null
    if (
      focus &&
      'selectedCard' in o &&
      (typeof o.selectedCard === 'number' || o.selectedCard === null) &&
      cardEditor &&
      layout
    ) {
      return {
        focus,
        selectedCard: o.selectedCard,
        cardEditor,
        layout,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {NoteTabUi | Null}
 */
const validNoteTabUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const focus = 'focus' in o ? validFocusStackOrNull(o.focus) : null
    if (
      focus &&
      'selectedNote' in o &&
      (typeof o.selectedNote === 'number' || o.selectedNote === null) &&
      'editingSelected' in o &&
      typeof o.editingSelected === 'boolean' &&
      'categoriesDialogOpen' in o &&
      typeof o.categoriesDialogOpen === 'boolean' &&
      'attributesDialogOpen' in o &&
      typeof o.attributesDialogOpen === 'boolean' &&
      'filterVisible' in o &&
      typeof o.filterVisible === 'boolean' &&
      'sortVisible' in o &&
      typeof o.sortVisible === 'boolean' &&
      'showAsList' in o &&
      typeof o.showAsList === 'boolean' &&
      'collapsedCategories' in o &&
      typeof o.collapsedCategories === 'boolean'
    ) {
      return {
        focus,
        selectedNote: o.selectedNote,
        editingSelected: o.editingSelected,
        categoriesDialogOpen: o.categoriesDialogOpen,
        attributesDialogOpen: o.attributesDialogOpen,
        filterVisible: o.filterVisible,
        sortVisible: o.sortVisible,
        showAsList: o.showAsList,
        collapsedCategories: o.collapsedCategories,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PlaceTabUi | null}
 */
const validPlaceTabUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const focus = 'focus' in o ? validFocusStackOrNull(o.focus) : null
    if (
      focus &&
      'selectedPlace' in o &&
      (typeof o.selectedPlace === 'number' || o.selectedPlace === null) &&
      'attributeDialogOpen' in o &&
      typeof o.attributeDialogOpen === 'boolean' &&
      'editingSelected' in o &&
      typeof o.editingSelected === 'boolean' &&
      'categoriesOpen' in o &&
      typeof o.categoriesOpen === 'boolean' &&
      'filterVisible' in o &&
      typeof o.filterVisible === 'boolean' &&
      'sortVisible' in o &&
      typeof o.sortVisible === 'boolean' &&
      'showAsList' in o &&
      typeof o.showAsList === 'boolean' &&
      'collapsedCategories' in o &&
      typeof o.collapsedCategories === 'boolean'
    ) {
      return {
        focus,
        selectedPlace: o.selectedPlace,
        attributeDialogOpen: o.attributeDialogOpen,
        editingSelected: o.editingSelected,
        categoriesOpen: o.categoriesOpen,
        filterVisible: o.filterVisible,
        sortVisible: o.sortVisible,
        showAsList: o.showAsList,
        collapsedCategories: o.collapsedCategories,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {TagTabUi | Null}
 */
const validTagTabUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const focus = 'focus' in o ? validFocusStackOrNull(o.focus) : null
    if (
      focus &&
      'selectedTag' in o &&
      (typeof o.selectedTag === 'number' || o.selectedTag === null) &&
      'editingSelectedTab' in o &&
      typeof o.editingSelectedTab === 'boolean'
    ) {
      return {
        focus,
        selectedTag: o.selectedTag,
        editingSelectedTab: o.editingSelectedTab,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {TemplateModalUi | null}
 */
const validTemplateModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('expanded' in o && typeof o.expanded === 'boolean') {
      return {
        expanded: o.expanded,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ActConfigModalUi | Null}
 */
const validActConfigModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('open' in o && typeof o.open === 'boolean') {
      return {
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ResturctureTimelineModalUi | Null}
 */
const validRestructureTimelineModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('open' in o && typeof o.open === 'boolean') {
      return {
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {DashboardModalUi | Null}
 */
const validDashboardModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'view' in o &&
      (o.view === null ||
        o.view === 'proOnboarding' ||
        o.view === 'account' ||
        o.view === 'chooser' ||
        o.view === 'explain' ||
        o.view === 'recent' ||
        o.view === 'files' ||
        o.view === 'options' ||
        o.view === 'backups' ||
        o.view === 'help')
    ) {
      return {
        view: o.view,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ConflictResolutionModalUi | Null}
 */
const validConflictResolutionModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('open' in o && typeof o.open === 'boolean') {
      return {
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @template A
 * @param {function(unknown): A | Null} isValidImportableOrNull
 * @return {function(Record<String, Importable<A>> | Null, [String, unknown]): (Record<String, Importable<A>> | Null)}
 */
const accumulateValidImportableDataToObjectOrNull = (isValidImportableOrNull) => (acc, next) => {
  if (acc === null) {
    return null
  } else {
    const [key, importable] = next
    if (importable && typeof importable === 'object') {
      const data = 'data' in importable ? isValidImportableOrNull(importable.data) : null
      if (
        data &&
        (('newId' in importable && typeof importable.newId === 'number') ||
          !('newId' in importable)) &&
        'isChecked' in importable &&
        typeof importable.isChecked === 'boolean'
      ) {
        return {
          ...acc,
          [key]: {
            data,
            ...('newId' in importable && typeof importable.newId === 'number'
              ? { newId: importable.newId }
              : {}),
            isChecked: importable.isChecked,
          },
        }
      } else {
        return null
      }
    } else {
      return null
    }
  }
}

/**
 * @param {unknown} o
 * @return {Record<String, Importable<PlottrFileBook>> | Null}
 */
const validImportModalUiDataBooksOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @type {function(Record<String, Importable<PlottrFileBook>> | Null, [String, unknown]): (Record<String, Importable<PlottrFileBook>> | Null)}
     */
    const accumulate = accumulateValidImportableDataToObjectOrNull(validBookOrNull)
    return Object.entries(o).reduce(accumulate, {})
  } else {
    return null
  }
}

/**
 * @template A
 * @param {function(unknown): A | Null} isValidImportableOrNull
 * @return {function(Array<Importable<A>> | Null, unknown): (Array<Importable<A>> | Null)}
 */
const accumulateValidImportableDataToArrayOrNull = (isValidImportableOrNull) => (acc, next) => {
  if (acc === null) {
    return null
  } else {
    const importable = next
    if (importable && typeof importable === 'object') {
      const data = 'data' in importable ? isValidImportableOrNull(importable.data) : null
      if (
        data &&
        (('newId' in importable && typeof importable.newId === 'number') ||
          !('newId' in importable)) &&
        'isChecked' in importable &&
        typeof importable.isChecked === 'boolean'
      ) {
        return [
          ...acc,
          {
            data,
            ...('newId' in importable && typeof importable.newId === 'number'
              ? { newId: importable.newId }
              : {}),
            isChecked: importable.isChecked,
          },
        ]
      } else {
        return null
      }
    } else {
      return null
    }
  }
}

/**
 * @param {unknown} o
 * @return {Array<Importable<NotesPlottrFileEntry>> | Null}
 */
const validImportModalUiDataNotesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    const accumulate = accumulateValidImportableDataToArrayOrNull(validNoteOrNull)
    return o.reduce(accumulate, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Importable<CharactersPlottrFileEntry>> | Null}
 */
const validImportModalUiDataCharactersOrNull = (o) => {
  if (o && Array.isArray(o)) {
    const accumulate = accumulateValidImportableDataToArrayOrNull(validCharacterOrNull)
    return o.reduce(accumulate, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Importable<PlacesPlottrFileEntry>> | Null}
 */
const validImportModalUiDataPlacesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    const accumulate = accumulateValidImportableDataToArrayOrNull(validPlaceOrNull)
    return o.reduce(accumulate, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Importable<TagsPlottrFileEntry>> | Null}
 */
const validImportModalUiDataTagsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    const accumulate = accumulateValidImportableDataToArrayOrNull(validTagOrNull)
    return o.reduce(accumulate, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Record<String, Importable<ImageRecord>> | Null}
 */
const validImportModalUiDataImagesOrNull = (o) => {
  if (o && typeof o === 'object') {
    const accumulate = accumulateValidImportableDataToObjectOrNull(validImageOrNull)
    return Object.entries(o).reduce(accumulate, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<Importable<LegacyCustomAttribute>> | Null}
 */
const validImportableLegacyCustomAttributesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    const accumulate = accumulateValidImportableDataToArrayOrNull(validLegacyCustomAttributeOrNull)
    return o.reduce(accumulate, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ImportableLegacyCustomAttributes | Null}
 */
const validImportModalUiDataCustomAttributes = (o) => {
  if (o && typeof o === 'object') {
    const lines = 'lines' in o ? validImportableLegacyCustomAttributesOrNull(o.lines) : null
    const scenes = 'scenes' in o ? validImportableLegacyCustomAttributesOrNull(o.scenes) : null
    const places = 'places' in o ? validImportableLegacyCustomAttributesOrNull(o.places) : null
    const characters =
      'characters' in o ? validImportableLegacyCustomAttributesOrNull(o.characters) : null
    const notes = 'notes' in o ? validImportableLegacyCustomAttributesOrNull(o.notes) : null
    if (lines && scenes && places && characters && notes) {
      return {
        lines,
        scenes,
        places,
        characters,
        notes,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ImportModalUiData | Null}
 */
const validImportModalUiDataOrNull = (o) => {
  if (o && typeof o === 'object') {
    const books = 'books' in o ? validImportModalUiDataBooksOrNull(o.books) : null
    const notes = 'notes' in o ? validImportModalUiDataNotesOrNull(o.notes) : null
    const characters =
      'characters' in o ? validImportModalUiDataCharactersOrNull(o.characters) : null
    const places = 'places' in o ? validImportModalUiDataPlacesOrNull(o.places) : null
    const tags = 'tags' in o ? validImportModalUiDataTagsOrNull(o.tags) : null
    const customAttributes =
      'customAttributes' in o ? validImportModalUiDataCustomAttributes(o.customAttributes) : null
    const images = 'images' in o ? validImportModalUiDataImagesOrNull(o.images) : null
    if (books && notes && characters && places && tags && customAttributes && images) {
      return {
        books,
        notes,
        characters,
        places,
        tags,
        customAttributes,
        images,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ImportModalUiBookData | Null}
 */
const validImportModalUiBookDataOrNull = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @type {Array<number> | Null}
     */
    const allIds =
      'allIds' in o && Array.isArray(o.allIds) && o.allIds.every((x) => typeof x === 'number')
        ? o.allIds
        : null
    const lines = 'lines' in o ? validLinesOrNull(o.lines) : null
    const beats = 'beats' in o ? validBeatsOrNull(o.beats) : null
    const cards = 'cards' in o ? validCardsOrNull(o.cards) : null
    const hierarchyLevels =
      'hierarchyLevels' in o ? validFullFileHierarchyLevelsOrNull(o.hierarchyLevels) : null
    if (allIds && lines && beats && cards && hierarchyLevels) {
      return {
        allIds,
        lines,
        beats,
        cards,
        hierarchyLevels,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ImportModalUi | Null}
 */
const validImportModalUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const data =
      'data' in o
        ? o.data === null
          ? null
          : validImportModalUiDataOrNull(o.data) || undefined
        : undefined
    const bookData =
      'bookData' in o
        ? o.bookData === null
          ? null
          : validImportModalUiBookDataOrNull(o.bookData) || undefined
        : undefined
    if (
      'type' in o &&
      (o.type === 'pltr' || o.type === null) &&
      data !== undefined &&
      bookData !== undefined &&
      'open' in o &&
      typeof o.open === 'boolean' &&
      'showProRecentFiles' in o &&
      typeof o.showProRecentFiles === 'boolean'
    ) {
      return {
        type: o.type,
        data,
        bookData,
        open: o.open,
        showProRecentFiles: o.showProRecentFiles,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {TimelineUi | Null}
 */
const validUITimelineOrNull = (object) => {
  if (object && typeof object === 'object') {
    const pinnedPlotlines =
      'pinnedPlotlines' in object ? validPinnedPlotlinesOrNull(object.pinnedPlotlines) : null
    const size = 'size' in object ? validTimelineSizeOrNull(object.size) : null
    const view = 'view' in object ? validTimelineActViewOrNull(object.view) : null
    const actTab = 'actTab' in object && typeof object.actTab === 'number' ? object.actTab : null
    const focus = 'focus' in object ? validFocusStackOrNull(object.focus) : null
    const beatToDelete =
      'beatToDelete' in object &&
      (typeof object.beatToDelete === 'number' || object.beatToDelete === null)
        ? object.beatToDelete
        : undefined
    const contextMenuBeat =
      'contextMenuBeat' in object &&
      (typeof object.contextMenuBeat === 'number' || object.contextMenuBeat === null)
        ? object.contextMenuBeat
        : undefined
    const editingBeatId =
      'editingBeatId' in object &&
      (typeof object.editingBeatId === 'number' || object.editingBeatId === null)
        ? object.editingBeatId
        : undefined
    const beatHeadingTitleBeingEdited =
      'beatHeadingTitleBeingEdited' in object &&
      (typeof object.beatHeadingTitleBeingEdited === 'number' ||
        object.beatHeadingTitleBeingEdited === null)
        ? object.beatHeadingTitleBeingEdited
        : undefined
    const plotlineTitleBeingEdited =
      'plotlineTitleBeingEdited' in object &&
      (typeof object.plotlineTitleBeingEdited === 'number' ||
        object.plotlineTitleBeingEdited === null)
        ? object.plotlineTitleBeingEdited
        : undefined
    const smallTimelineCardColorSource =
      'smallTimelineCardColorSource' in object &&
      (object.smallTimelineCardColorSource === 'plotline' ||
        object.smallTimelineCardColorSource === 'card')
        ? object.smallTimelineCardColorSource
        : null
    const filterVisible =
      'filterVisible' in object && typeof object.filterVisible === 'boolean'
        ? object.filterVisible
        : null
    if (
      pinnedPlotlines &&
      beatToDelete !== undefined &&
      contextMenuBeat !== undefined &&
      editingBeatId !== undefined &&
      beatHeadingTitleBeingEdited !== undefined &&
      plotlineTitleBeingEdited !== undefined &&
      size &&
      view &&
      actTab !== null &&
      focus &&
      smallTimelineCardColorSource &&
      filterVisible !== null
    ) {
      return {
        pinnedPlotlines,
        size,
        view,
        actTab,
        focus,
        beatToDelete,
        contextMenuBeat,
        editingBeatId,
        beatHeadingTitleBeingEdited,
        plotlineTitleBeingEdited,
        smallTimelineCardColorSource,
        filterVisible,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {FamilyTreeStagePosition | Null}
 */
const validFamilyTreeStagePositionOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'x' in o &&
      (typeof o.x === 'number' || !('x' in o)) &&
      'y' in o &&
      (typeof o.y === 'number' || !('y' in o)) &&
      'scale' in o &&
      typeof o.scale === 'number'
    ) {
      return {
        ...('x' in o && typeof o.x === 'number' ? { x: o.x } : {}),
        ...('y' in o && typeof o.y === 'number' ? { y: o.y } : {}),
        scale: o.scale,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {FamilyTreeStagePositions | Null}
 */
const isValidStagePositions = (o) => {
  if (o && typeof o === 'object') {
    /**
     * @param {Record<String, FamilyTreeStagePosition> | Null} acc
     * @param {[String, unknown]} next
     * @return {Record<String, FamilyTreeStagePosition> | Null}
     */
    const accumulate = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const [key, position] = next
        if (position && typeof position === 'object') {
          const validFamilyTreeStagePosition = validFamilyTreeStagePositionOrNull(position)
          if (validFamilyTreeStagePosition) {
            return {
              ...acc,
              [key]: validFamilyTreeStagePosition,
            }
          } else {
            return null
          }
        } else {
          return null
        }
      }
    }
    return Object.entries(o).reduce(accumulate, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {FamilyTreeUi | Null}
 */
const validFamilyTreeUiOrNull = (o) => {
  if (o && typeof o === 'object') {
    const currentFamilyTree =
      'currentFamilyTree' in o &&
      (typeof o.currentFamilyTree === 'number' || o.currentFamilyTree === null)
        ? o.currentFamilyTree
        : undefined
    const selectedRelationshipType =
      'selectedRelationshipType' in o &&
      (typeof o.selectedRelationshipType === 'string' || o.selectedRelationshipType === null)
        ? o.selectedRelationshipType
        : undefined
    const showColourPicker =
      'showColourPicker' in o && typeof o.showColourPicker === 'boolean' ? o.showColourPicker : null
    const stagePositions = 'stagePositions' in o ? isValidStagePositions(o.stagePositions) : null
    const idOfTreeWhoseNameIsBeingEdited =
      'idOfTreeWhoseNameIsBeingEdited' in o &&
      (typeof o.idOfTreeWhoseNameIsBeingEdited === 'number' ||
        o.idOfTreeWhoseNameIsBeingEdited === null)
        ? o.idOfTreeWhoseNameIsBeingEdited
        : undefined
    if (
      currentFamilyTree !== undefined &&
      selectedRelationshipType !== undefined &&
      stagePositions &&
      showColourPicker !== null &&
      idOfTreeWhoseNameIsBeingEdited !== undefined
    ) {
      return {
        currentFamilyTree,
        selectedRelationshipType,
        stagePositions,
        showColourPicker,
        idOfTreeWhoseNameIsBeingEdited,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {DocumentationUi | Null}
 */
const validDocumentationOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'documentationType' in o &&
      o.documentationType === 'advanced-export' &&
      'open' in o &&
      typeof o.open === 'boolean'
    ) {
      return {
        documentationType: o.documentationType,
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PreviewPanelCoord | Null}
 */
const validPreviewPanelCoordsOrNull = (o) => {
  if (o && typeof o === 'object') {
    const kind =
      'kind' in o &&
      (o.kind === 'FLOATING_PREVIEW_PANEL_COORD' ||
        o.kind === 'DOCKED_PREVIEW_PANEL_SLOT' ||
        o.kind === 'MINIMISED_PREVIEW_PANEL_POSITION')
        ? o.kind
        : null
    switch (kind) {
      case 'FLOATING_PREVIEW_PANEL_COORD': {
        if ('left' in o && typeof o.left === 'number' && 'top' in o && typeof o.top === 'number') {
          return {
            kind,
            left: o.left,
            top: o.top,
          }
        } else {
          return null
        }
      }
      case 'DOCKED_PREVIEW_PANEL_SLOT':
      case 'MINIMISED_PREVIEW_PANEL_POSITION': {
        return {
          kind,
        }
      }
      default: {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PreviewPanelItem | Null}
 */
const validPreviewPanelItemOrNull = (o) => {
  if (o && typeof o === 'object') {
    const coords = 'coords' in o ? validPreviewPanelCoordsOrNull(o.coords) : null
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'kind' in o &&
      (o.kind === 'notes' ||
        o.kind === 'cards' ||
        o.kind === 'characters' ||
        o.kind === 'places') &&
      'shrinked' in o &&
      typeof o.shrinked === 'boolean' &&
      coords
    ) {
      return {
        id: o.id,
        kind: o.kind,
        shrinked: o.shrinked,
        coords,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<PreviewPanelItem> | Null}
 */
const validPreviewPanelItemsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<PreviewPanelItem> | Null} acc
     * @param {unknown} next
     * @return {Array<PreviewPanelItem> | Null}
     */
    const accumulateValidPreviewPanelsOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validPreviewPanelItem = validPreviewPanelItemOrNull(next)
        if (validPreviewPanelItem) {
          return [...acc, validPreviewPanelItem]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidPreviewPanelsOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PreviewPanels | Null}
 */
const validPreviewPanelsOrNull = (o) => {
  if (o && typeof o === 'object') {
    const panels = 'panels' in o ? validPreviewPanelItemsOrNull(o.panels) : null
    if (panels && 'showConfirmModal' in o && typeof o.showConfirmModal === 'boolean') {
      return {
        panels,
        showConfirmModal: o.showConfirmModal,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {QuickEditModal | Null}
 */
const validQuickEditModalOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'open' in o &&
      typeof o.open === 'boolean' &&
      'id' in o &&
      (typeof o.id === 'number' || o.id === null) &&
      'kind' in o &&
      (o.kind === 'notes' ||
        o.kind === 'cards' ||
        o.kind === 'characters' ||
        o.kind === 'places' ||
        o.kind === null)
    ) {
      return {
        open: o.open,
        id: o.id,
        kind: o.kind,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {BookmarkItem | Null}
 */
const validBookmarkItemOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'kind' in o &&
      (o.kind === 'notes' ||
        o.kind === 'cards' ||
        o.kind === 'characters' ||
        o.kind === 'places') &&
      (('title' in o && (typeof o.title === 'string' || typeof o.title === 'undefined')) ||
        !('title' in o)) &&
      (('bookTitles' in o &&
        ((Array.isArray(o.bookTitles) && o.bookTitles.every((t) => typeof t === 'string')) ||
          o.bookTitles === null ||
          typeof o.bookTitles === 'undefined')) ||
        !('bookTitles' in o)) &&
      (('bookId' in o &&
        (typeof o.bookId === 'string' || o.bookId === null || typeof o.bookId === 'undefined')) ||
        !('bookId' in o))
    ) {
      return {
        id: o.id,
        kind: o.kind,
        ...('title' in o && typeof o.title === 'string' ? { title: o.title } : {}),
        ...('bookTitles' in o &&
        ((Array.isArray(o.bookTitles) && o.bookTitles.every((t) => typeof t === 'string')) ||
          o.bookTitles === null)
          ? { bookTitles: o.bookTitles }
          : {}),
        ...('bookId' in o && (typeof o.bookId === 'string' || o.bookId === null)
          ? { bookId: o.bookId }
          : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<BookmarkItem> | Null}
 */
const validBookmarkItemsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<BookmarkItem> | Null} acc
     * @param {unknown} next
     * @return {Array<BookmarkItem> | Null}
     */
    const accumulateValidBookmarkItemOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validBookmarkItem = validBookmarkItemOrNull(next)
        if (validBookmarkItem) {
          return [...acc, validBookmarkItem]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidBookmarkItemOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<BookmarkEntityKind> | Null}
 */
const validCollapsedSectionOrNull = (o) => {
  if (
    o &&
    Array.isArray(o) &&
    o.every((x) => x === 'notes' || x === 'cards' || x === 'characters' || x === 'places')
  ) {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Bookmarks | Null}
 */
const validBookmarksOrNull = (o) => {
  if (o && typeof o === 'object') {
    const items = 'items' in o ? validBookmarkItemsOrNull(o.items) : null
    const collapsedSection =
      'collapsedSection' in o ? validCollapsedSectionOrNull(o.collapsedSection) : null
    if (items && collapsedSection) {
      return {
        items,
        collapsedSection,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {IdeaItem | Null}
 */
const validIdeaItemOrNull = (o) => {
  if (o && typeof o === 'object') {
    const description = 'description' in o ? validSlateOrStringOrNull(o.description) : null
    if (
      'id' in o &&
      typeof o.id === 'number' &&
      'title' in o &&
      typeof o.title === 'string' &&
      (description || description === '') &&
      'lastEdited' in o &&
      typeof o.lastEdited === 'number' &&
      (('imageId' in o && typeof o.imageId === 'number') || !('imageIdea' in o))
    ) {
      return {
        id: o.id,
        title: o.title,
        description,
        lastEdited: o.lastEdited,
        ...('imageId' in o && typeof o.imageId === 'string' ? { imageId: o.imageId } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<IdeaItem> | Null}
 */
const validIdeasOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<IdeaItem> | Null} acc
     * @param {unknown} next
     * @return {Array<IdeaItem> | Null}
     */
    const accumulateValidIdeasOrNull = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validIdea = validIdeaItemOrNull(next)
        if (validIdea) {
          return [...acc, validIdea]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidIdeasOrNull, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {IdeaModal | Null}
 */
const validModalOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      'open' in o &&
      typeof o.open === 'boolean' &&
      'id' in o &&
      (typeof o.id === 'number' || o.id === null)
    ) {
      return {
        id: o.id,
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {String | Slate | Null}
 */
const validTextOrParagraphOrNull = (o) => {
  if (o && typeof o === 'object') {
    const validSlate = validSlateOrNull(o)
    if (validSlate) {
      return validSlate
    } else {
      return null
    }
  } else if (typeof o === 'string') {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} plotlineTemplateData
 * @return {PlotlineTemplateData | null}
 */
const validPlotlineTemplateDataOrNull = (plotlineTemplateData) => {
  if (plotlineTemplateData && typeof plotlineTemplateData === 'object') {
    const cards = 'cards' in plotlineTemplateData && validCardsOrNull(plotlineTemplateData.cards)
    const beats = 'beats' in plotlineTemplateData && validBeatsOrNull(plotlineTemplateData.beats)
    const lines = 'lines' in plotlineTemplateData && validLinesOrNull(plotlineTemplateData.lines)
    const hierarchyLevels =
      'hierarchyLevels' in plotlineTemplateData &&
      validHierarchyLevelsOrNull(plotlineTemplateData.hierarchyLevels)

    if (plotlineTemplateData && cards && beats && lines && hierarchyLevels) {
      return {
        cards,
        beats,
        lines,
        hierarchyLevels,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<TemplateCategory> | Null}
 */
const validTemplateCategoriesOrNull = (o) => {
  if (Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (
        next === 'children' ||
        next === 'comedy' ||
        next === 'general' ||
        next === 'horror' ||
        next === 'romance' ||
        next === 'mystery' ||
        next === 'action' ||
        next === 'screenplay' ||
        next === 'shortstory' ||
        next === 'playwriting' ||
        next === 'poetry' ||
        next === 'foundational' ||
        next === 'screenwriting'
      ) {
        return [...acc, next]
      } else {
        return null
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} template
 * @return {PlotlineTemplate | Null}
 */
const validPlotlinesTemplateOrNull = (template) => {
  const templateData =
    template && typeof template === 'object' && 'templateData' in template
      ? validPlotlineTemplateDataOrNull(template.templateData)
      : null
  const templateCategories =
    template && typeof template === 'object' && 'categories' in template
      ? validTemplateCategoriesOrNull(template.categories)
      : null
  if (
    template &&
    typeof template === 'object' &&
    'id' in template &&
    typeof template.id === 'string' &&
    'type' in template &&
    template.type === 'plotlines' &&
    'name' in template &&
    typeof template.name === 'string' &&
    'description' in template &&
    typeof template.description === 'string' &&
    'version' in template &&
    typeof template.version === 'string' &&
    templateCategories &&
    templateData &&
    'initialVersion' in template &&
    typeof template.initialVersion === 'string' &&
    'appliedMigrations' in template &&
    Array.isArray(template.appliedMigrations) &&
    template.appliedMigrations.every((x) => typeof x === 'string') &&
    'templateDataVersion' in template &&
    typeof template.templateDataVersion === 'string'
  ) {
    return {
      id: template.id,
      type: template.type,
      name: template.name,
      description: template.description,
      version: template.version,
      categories: templateCategories,
      initialVersion: template.initialVersion,
      appliedMigrations: template.appliedMigrations,
      builtIn: true,
      templateData,
      templateDataVersion: template.templateDataVersion,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} template
 * @return {SceneTemplate  | Null}
 */
const validSceneTemplateOrNull = (template) => {
  const attributes =
    template && typeof template === 'object' && 'attributes' in template
      ? validTemplateAttributeArrayOrNull(template.attributes)
      : null
  if (
    template &&
    typeof template === 'object' &&
    'id' in template &&
    typeof template.id === 'string' &&
    'type' in template &&
    template.type === 'scenes' &&
    'version' in template &&
    typeof template.version === 'string' &&
    'name' in template &&
    typeof template.name === 'string' &&
    'description' in template &&
    typeof template.description === 'string' &&
    (('link' in template && typeof template.link === 'string') || !('link' in template)) &&
    attributes
  ) {
    return {
      id: template.id,
      type: template.type,
      version: template.version,
      name: template.name,
      description: template.description,
      ...('link' in template && typeof template.link === 'string' ? { link: template.link } : {}),
      attributes,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} projectTemplateData
 * @return {ProjectTemplateData | Null}
 */
const validProjectTemplateDataOrNull = (projectTemplateData) => {
  if (projectTemplateData && typeof projectTemplateData === 'object') {
    const cards = 'cards' in projectTemplateData && validCardsOrNull(projectTemplateData.cards)
    const beats = 'beats' in projectTemplateData && validBeatsOrNull(projectTemplateData.beats)
    const lines = 'lines' in projectTemplateData && validLinesOrNull(projectTemplateData.lines)
    const hierarchyLevels = !('hierarchyLevels' in projectTemplateData)
      ? createNewFileHierarchies()
      : validFullFileHierarchyLevelsOrNull(projectTemplateData.hierarchyLevels)
    const customAttributes = !('customAttributes' in projectTemplateData)
      ? defaultCustomAttributes
      : validCustomAttributesOrNull(projectTemplateData.customAttributes)
    const tags = !('tags' in projectTemplateData) ? [] : validTagsOrNull(projectTemplateData.tags)
    const notes = !('notes' in projectTemplateData)
      ? []
      : validNotesOrNull(projectTemplateData.notes)
    const categories = !('categories' in projectTemplateData)
      ? createNewFileCategories()
      : validCategoriesOrNull(projectTemplateData.categories)
    const images = !('images' in projectTemplateData)
      ? {}
      : validImagesOrNull(projectTemplateData.images)
    const books = !('books' in projectTemplateData)
      ? undefined
      : validBooksOrNull(projectTemplateData.books)
    if ('books' in projectTemplateData && !books) {
      return null
    } else if (
      (books || books === undefined) &&
      cards &&
      beats &&
      lines &&
      hierarchyLevels &&
      customAttributes &&
      tags &&
      notes &&
      categories &&
      images
    ) {
      return {
        cards,
        beats,
        lines,
        hierarchyLevels,
        customAttributes,
        tags,
        notes,
        categories,
        images,
        ...(books ? { books } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} template
 * @return {ProjectTemplate | Null}
 */
const validProjectTemplateOrNull = (template) => {
  if (template && typeof template === 'object') {
    const templateData =
      'templateData' in template ? validProjectTemplateDataOrNull(template.templateData) : null
    if (
      'id' in template &&
      typeof template.id === 'string' &&
      'name' in template &&
      typeof template.name === 'string' &&
      'description' in template &&
      typeof template.description === 'string' &&
      'version' in template &&
      typeof template.version === 'string' &&
      'link' in template &&
      typeof template.link === 'string' &&
      'templateData' in template &&
      templateData &&
      'initialVersion' in template &&
      typeof template.initialVersion === 'string' &&
      'appliedMigrations' in template &&
      Array.isArray(template.appliedMigrations) &&
      template.appliedMigrations.every((x) => typeof x === 'string') &&
      'templateDataVersion' in template &&
      typeof template.templateDataVersion === 'string'
    ) {
      return {
        id: template.id,
        type: 'project',
        name: template.name,
        description: template.description,
        version: template.version,
        templateData,
        link: template.link,
        initialVersion: template.initialVersion,
        appliedMigrations: template.appliedMigrations,
        templateDataVersion: template.templateDataVersion,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} template
 * @return {BuiltInTemplate | Null}
 */
export const validBuiltInTemplateOrNull = (template) => {
  if (template && typeof template === 'object' && 'type' in template) {
    switch (template.type) {
      case 'project': {
        return validProjectTemplateOrNull(template)
      }
      case 'characters': {
        return validCharactersTemplateOrNull(template)
      }
      case 'plotlines': {
        return validPlotlinesTemplateOrNull(template)
      }
      case 'scenes': {
        return validSceneTemplateOrNull(template)
      }
      default: {
        return null
      }
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<BuiltInTemplate | CustomTemplate> | Null}
 */
const validTemplatesOrNull = (o) => {
  if (o && Array.isArray(o)) {
    /**
     * @param {Array<BuiltInTemplate | CustomTemplate> | Null} acc
     * @param {unknown} next
     * @return {Array<BuiltInTemplate | CustomTemplate> | Null}
     */
    const accumulateValidTemplates = (acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validCustomTemplate = validCustomTemplateOrNull(next)
        const validBuiltInTemplate = validBuiltInTemplateOrNull(next)
        if (validBuiltInTemplate) {
          return [...acc, validBuiltInTemplate]
        } else if (validCustomTemplate) {
          return [...acc, validCustomTemplate]
        } else {
          return null
        }
      }
    }
    return o.reduce(accumulateValidTemplates, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<LegacyCustomAttribute> | Null}
 */
const validLegacyCustomAttributesOrNull = (o) => {
  if (Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const legacyCustomAttribute = validLegacyCustomAttributeOrNull(next)
        if (legacyCustomAttribute) {
          return [...acc, legacyCustomAttribute]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {IdeaBaseData | Null}
 */
const validIdeaConfirmModalBaseDataOrNull = (o) => {
  if (o && typeof o === 'object') {
    const description = 'description' in o ? validTextOrParagraphOrNull(o.description) : null
    const customAttributes =
      'customAttributes' in o ? validLegacyCustomAttributesOrNull(o.customAttributes) : undefined
    const templates = 'templates' in o ? validTemplatesOrNull(o.templates) : undefined
    const pocketIdeaId =
      'pocketIdeaId' in o ? validUniversalIdeaIdOrNull(o.pocketIdeaId) : undefined
    const id =
      'id' in o && typeof o.id === 'number' ? o.id : !('id' in o) ? undefined : 'INVALID_ID'
    const color =
      'color' in o && (typeof o.color === 'string' || o.color === null)
        ? o.color
        : !('color' in o)
          ? undefined
          : 'INVALID_COLOR'
    if (
      'title' in o &&
      typeof o.title === 'string' &&
      description !== null &&
      customAttributes !== null &&
      (('audioClipId' in o && (typeof o.audioClipId === 'number' || o.audioClipId === null)) ||
        !('audioClip' in o)) &&
      templates !== null &&
      id !== 'INVALID_ID' &&
      color !== 'INVALID_COLOR' &&
      pocketIdeaId !== null
    ) {
      return {
        title: o.title,
        description,
        ...('customAttributes' in o ? { customAttributes } : {}),
        ...('audioClipId' in o && typeof o.audioClipId === 'number'
          ? { audioClipId: o.audioClipId }
          : {}),
        ...('templates' in o ? { templates } : {}),
        ...('id' in o ? { id } : {}),
        ...('color' in o ? { color } : {}),
        ...('pocketIdeaId' in o && pocketIdeaId ? { pocketIdeaId } : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {IdeaConfirmModal | Null}
 */
const validConfirmModalOrNull = (o) => {
  if (o && typeof o === 'object') {
    const data =
      'data' in o
        ? o.data === null
          ? null
          : validIdeaConfirmModalBaseDataOrNull(o.data) || undefined
        : undefined
    if (
      'open' in o &&
      typeof o.open === 'boolean' &&
      'isDeleting' in o &&
      typeof o.isDeleting === 'boolean' &&
      'id' in o &&
      (typeof o.id === 'number' || o.id === null) &&
      'section' in o &&
      (o.section === 'notes' ||
        o.section === 'cards' ||
        o.section === 'characters' ||
        o.section === 'places' ||
        o.section === null) &&
      data !== undefined
    ) {
      return {
        open: o.open,
        isDeleting: o.isDeleting,
        id: o.id,
        section: o.section,
        data,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {UniversalIdeaId | Null}
 */
const validUniversalIdeaIdOrNull = (o) => {
  if (typeof o === 'object' && o) {
    if (
      'deviceId' in o &&
      typeof o.deviceId === 'string' &&
      'ideaId' in o &&
      typeof o.ideaId === 'number'
    ) {
      return {
        deviceId: o.deviceId,
        ideaId: o.ideaId,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<UniversalIdeaId> | Null}
 */
const validPocketIdeasUsedOrNull = (o) => {
  if (Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const ideaId = validUniversalIdeaIdOrNull(next)
        if (ideaId) {
          return [...acc, ideaId]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {IdeaBoard | Null}
 */
const validIdeaBoardOrNull = (o) => {
  if (o && typeof o === 'object') {
    const ideas = 'ideas' in o ? validIdeasOrNull(o.ideas) : null
    const modal = 'modal' in o ? validModalOrNull(o.modal) : null
    const confirmModal = 'confirmModal' in o ? validConfirmModalOrNull(o.confirmModal) : null
    const pocketIdeasUsed =
      'pocketIdeasUsed' in o ? validPocketIdeasUsedOrNull(o.pocketIdeasUsed) : null
    if (
      'open' in o &&
      typeof o.open === 'boolean' &&
      ideas &&
      pocketIdeasUsed &&
      modal &&
      confirmModal &&
      'showCardDescription' in o &&
      typeof o.showCardDescription === 'boolean' &&
      'shouldShowPanelsWithIdeaBoard' in o &&
      typeof o.shouldShowPanelsWithIdeaBoard === 'boolean'
    ) {
      return {
        open: o.open,
        ideas,
        pocketIdeasUsed,
        modal,
        confirmModal,
        showCardDescription: o.showCardDescription,
        shouldShowPanelsWithIdeaBoard: o.shouldShowPanelsWithIdeaBoard,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {PlottrThemes | Null}
 */
const validPlottrThemeOrNull = (o) => {
  if (o === 'light-blue' || o === 'light-green' || o === 'light-orange' || o === null) {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {MobileControlsModal | Null}
 */
const validMobileControlsModalOrNull = (o) => {
  if (o && typeof o === 'object') {
    if ('open' in o && typeof o.open === 'boolean') {
      return {
        open: o.open,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {PlottrFileUi | Null}
 */
export const validUiOrNull = (object) => {
  if (object && typeof object === 'object') {
    const currentView =
      'currentView' in object && object.currentView
        ? validPlottrTabOrNull(object.currentView)
        : null
    const currentTimeline =
      'currentTimeline' in object && object.currentTimeline
        ? validTimelineIdOrNull(object.currentTimeline)
        : null
    const timelineIsExpanded =
      'timelineIsExpanded' in object && typeof object.timelineIsExpanded === 'boolean'
        ? object.timelineIsExpanded
        : null
    const orientation =
      'orientation' in object && object.orientation
        ? validTimelineOrientationOrNull(object.orientation)
        : null
    const darkMode =
      'darkMode' in object && typeof object.darkMode === 'boolean' ? object.darkMode : null
    const characterSort =
      'characterSort' in object && typeof object.characterSort === 'string'
        ? object.characterSort
        : null
    const characterFilter =
      'characterFilter' in object
        ? object.characterFilter === null
          ? null
          : (object.characterFilter && validCharacterFilterOrNull(object.characterFilter)) ||
            undefined
        : undefined
    const placeSort =
      'placeSort' in object && typeof object.placeSort === 'string' ? object.placeSort : null
    const placeFilter =
      'placeFilter' in object
        ? object.placeFilter === null
          ? null
          : object.placeFilter
            ? validPlaceFilterOrNull(object.placeFilter)
            : undefined
        : undefined
    const noteSort =
      'noteSort' in object && typeof object.noteSort === 'string' ? object.noteSort : null
    const noteFilter =
      'noteFilter' in object
        ? object.noteFilter === null
          ? null
          : object.noteFilter
            ? validNoteFilterOrNull(object.noteFilter)
            : undefined
        : undefined
    const outlineFilter =
      'outlineFilter' in object
        ? Array.isArray(object.outlineFilter) &&
          object.outlineFilter.every((x) => typeof x === 'number')
          ? object.outlineFilter
          : object.outlineFilter === null
            ? null
            : undefined
        : undefined
    const timelineFilter =
      'timelineFilter' in object
        ? object.timelineFilter
          ? validTimelineFilterOrNull(object.timelineFilter)
          : object.timelineFilter === null
            ? null
            : undefined
        : undefined
    const timelineScrollPosition =
      'timelineScrollPosition' in object && object.timelineScrollPosition
        ? validTimelineScrollPositionOrNull(object.timelineScrollPosition)
        : null
    const timeline =
      'timeline' in object && object.timeline ? validUITimelineOrNull(object.timeline) : null
    const searchTerms =
      'searchTerms' in object && object.searchTerms
        ? validUISearchTermsOrNull(object.searchTerms)
        : null
    const attributeTabs =
      'attributeTabs' in object && object.attributeTabs
        ? validUiAttributeTabsOrNull(object.attributeTabs)
        : null
    const characterTab =
      'characterTab' in object && object.characterTab
        ? validCharacterTabUiOrNull(object.characterTab)
        : null
    const customAttributeOrder =
      'customAttributeOrder' in object && object.customAttributeOrder
        ? validCustomAttributeOrderOrNull(object.customAttributeOrder)
        : null
    const cardDialog =
      'cardDialog' in object && object.cardDialog
        ? validCardDialogUiOrNull(object.cardDialog)
        : null
    const collaborators =
      'collaborators' in object && object.collaborators
        ? object.collaborators === null
          ? null
          : validUiCollaboratorsOrNull(object.collaborators) || undefined
        : undefined
    const bookDialog =
      'bookDialog' in object && object.bookDialog ? validBookDialogOrNull(object.bookDialog) : null
    const searchDialog =
      'searchDialog' in object && object.searchDialog
        ? validSearchDialogUiOrNull(object.searchDialog)
        : null
    const projectTab =
      'projectTab' in object && object.projectTab ? validProjectTabOrNull(object.projectTab) : null
    const outlineTab =
      'outlineTab' in object && object.outlineTab ? validOutlineTabOrNull(object.outlineTab) : null
    const noteTab =
      'noteTab' in object && object.noteTab ? validNoteTabUiOrNull(object.noteTab) : null
    const placeTab =
      'placeTab' in object && object.placeTab ? validPlaceTabUiOrNull(object.placeTab) : null
    const tagTab = 'tagTab' in object && object.tagTab ? validTagTabUiOrNull(object.tagTab) : null
    const outlineScrollPosition =
      'outlineScrollPosition' in object && typeof object.outlineScrollPosition === 'number'
        ? object.outlineScrollPosition
        : null
    const templateModal =
      'templateModal' in object && object.templateModal
        ? validTemplateModalUiOrNull(object.templateModal)
        : null
    const actConfigModal =
      'actConfigModal' in object && object.actConfigModal
        ? validActConfigModalUiOrNull(object.actConfigModal)
        : null
    const resturctureTimelineModal =
      'resturctureTimelineModal' in object && object.resturctureTimelineModal
        ? validRestructureTimelineModalUiOrNull(object.resturctureTimelineModal)
        : null
    const dashboardModal =
      'dashboardModal' in object && object.dashboardModal
        ? validDashboardModalUiOrNull(object.dashboardModal)
        : null
    const conflictResolutionModal =
      'conflictResolutionModal' in object && object.conflictResolutionModal
        ? validConflictResolutionModalUiOrNull(object.conflictResolutionModal)
        : null
    const importModal =
      'importModal' in object && object.importModal
        ? validImportModalUiOrNull(object.importModal)
        : null
    const familyTree =
      'familyTree' in object && object.familyTree
        ? validFamilyTreeUiOrNull(object.familyTree)
        : null
    const documentation =
      'documentation' in object && object.documentation
        ? validDocumentationOrNull(object.documentation)
        : null
    const outlinePlanScrollPosition =
      'outlinePlanScrollPosition' in object && typeof object.outlinePlanScrollPosition === 'number'
        ? object.outlinePlanScrollPosition
        : null
    const attributesDialogIsOpen =
      'attributesDialogIsOpen' in object && typeof object.attributesDialogIsOpen === 'boolean'
        ? object.attributesDialogIsOpen
        : null
    const showPreviewPanels =
      'showPreviewPanels' in object && typeof object.showPreviewPanels === 'boolean'
        ? object.showPreviewPanels
        : null
    const previewPanels =
      'previewPanels' in object ? validPreviewPanelsOrNull(object.previewPanels) : null
    const quickEditModal =
      'quickEditModal' in object ? validQuickEditModalOrNull(object.quickEditModal) : null
    const bookmarks = 'bookmarks' in object ? validBookmarksOrNull(object.bookmarks) : null
    const ideaBoard = 'ideaBoard' in object ? validIdeaBoardOrNull(object.ideaBoard) : null
    const theme =
      'theme' in object
        ? object.theme === null
          ? null
          : validPlottrThemeOrNull(object.theme) || undefined
        : undefined
    const mobileControlsModal =
      'mobileControlsModal' in object
        ? validMobileControlsModalOrNull(object.mobileControlsModal)
        : null
    if (
      currentView &&
      currentTimeline !== null &&
      timelineIsExpanded !== null &&
      orientation &&
      darkMode !== null &&
      characterSort &&
      characterFilter !== undefined &&
      placeSort &&
      placeFilter !== undefined &&
      noteSort &&
      noteFilter !== undefined &&
      outlineFilter !== undefined &&
      timelineFilter !== undefined &&
      timelineScrollPosition &&
      timeline &&
      searchTerms &&
      attributeTabs &&
      characterTab &&
      customAttributeOrder &&
      cardDialog &&
      collaborators !== undefined &&
      bookDialog &&
      searchDialog &&
      projectTab &&
      outlineTab &&
      noteTab &&
      placeTab &&
      tagTab &&
      outlineScrollPosition !== null &&
      templateModal &&
      actConfigModal &&
      resturctureTimelineModal &&
      dashboardModal &&
      conflictResolutionModal &&
      importModal &&
      familyTree &&
      documentation &&
      outlinePlanScrollPosition !== null &&
      attributesDialogIsOpen !== null &&
      showPreviewPanels !== null &&
      previewPanels &&
      quickEditModal &&
      bookmarks &&
      ideaBoard &&
      theme !== undefined &&
      mobileControlsModal
    ) {
      return {
        currentView,
        currentTimeline,
        timelineIsExpanded,
        orientation,
        darkMode,
        characterSort,
        characterFilter,
        placeSort,
        placeFilter,
        noteSort,
        noteFilter,
        outlineFilter,
        timelineFilter,
        timelineScrollPosition,
        timeline,
        searchTerms,
        attributeTabs,
        characterTab,
        customAttributeOrder,
        cardDialog,
        collaborators,
        bookDialog,
        searchDialog,
        projectTab,
        outlineTab,
        noteTab,
        placeTab,
        tagTab,
        outlineScrollPosition,
        templateModal,
        actConfigModal,
        resturctureTimelineModal,
        dashboardModal,
        conflictResolutionModal,
        importModal,
        familyTree,
        documentation,
        outlinePlanScrollPosition,
        attributesDialogIsOpen,
        showPreviewPanels,
        previewPanels,
        quickEditModal,
        bookmarks,
        ideaBoard,
        theme,
        mobileControlsModal,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {FeatureFlags | Null}
 */
export const validFeatureFlagsOrNull = (object) => {
  if (object && typeof object === 'object') {
    /**
     * @param {FeatureFlags | Null} acc
     * @param {[String, unknown]} next
     * @return {FeatureFlags | Null}
     */
    const accumulateValidFeatureFlagsOrNull = (acc, next) => {
      const [key, value] = next
      if (acc === null) {
        return null
      } else if (typeof value === 'boolean') {
        return {
          ...acc,
          [key]: value,
        }
      } else {
        return null
      }
    }
    return Object.entries(object).reduce(accumulateValidFeatureFlagsOrNull, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {FilePermission | Null}
 */
const validPermissionOrNull = (o) => {
  if (o === 'owner' || o === 'collaborator' || o === 'viewer') {
    return o
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {ShareRecord | Null}
 */
const validShareRecordOrNull = (o) => {
  if (o && typeof o === 'object') {
    const validPermission = 'permission' in o ? validPermissionOrNull(o.permission) : null
    if ('emailAddress' in o && typeof o.emailAddress === 'string' && validPermission) {
      return {
        emailAddress: o.emailAddress,
        permission: validPermission,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} o
 * @return {Array<ShareRecord> | Null}
 */
const validShareRecordsOrNull = (o) => {
  if (o && Array.isArray(o)) {
    return o.reduce((acc, next) => {
      if (acc === null) {
        return null
      } else {
        const validShareRecord = validShareRecordOrNull(next)
        if (validShareRecord) {
          return [...acc, validShareRecord]
        } else {
          return null
        }
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {PlottrFileFile | Null}
 */
export const validFileOrNull = (object) => {
  if (object && typeof object === 'object') {
    const fileName =
      'fileName' in object && typeof object.fileName === 'string' ? object.fileName : undefined
    const loaded =
      'loaded' in object && typeof object.loaded === 'boolean' ? object.loaded : undefined
    const dirty = 'dirty' in object && typeof object.dirty === 'boolean' ? object.dirty : undefined
    const version =
      'version' in object && typeof object.version === 'string' ? object.version : undefined
    const id = 'id' in object && typeof object.id === 'string' ? object.id : undefined
    const appliedMigrations =
      'appliedMigrations' in object &&
      Array.isArray(object.appliedMigrations) &&
      object.appliedMigrations.every((x) => typeof x === 'string')
        ? object.appliedMigrations
        : undefined
    const initialVersion =
      'initialVersion' in object && typeof object.initialVersion === 'string'
        ? object.initialVersion
        : undefined
    const isCloudFile =
      'isCloudFile' in object && typeof object.isCloudFile === 'boolean'
        ? object.isCloudFile
        : undefined

    const shareRecords =
      'shareRecords' in object ? validShareRecordsOrNull(object.shareRecords) : undefined
    const versionStamp =
      'versionStamp' in object && typeof object.versionStamp === 'string'
        ? object.versionStamp
        : undefined
    const originalVersionStamp =
      'originalVersionStamp' in object && typeof object.originalVersionStamp === 'string'
        ? object.originalVersionStamp
        : undefined
    const deleted =
      'deleted' in object && typeof object.deleted === 'boolean' ? object.deleted : undefined
    const isResuming =
      'isResuming' in object && typeof object.isResuming === 'boolean'
        ? object.isResuming
        : undefined

    if (typeof fileName === 'string' && version && shareRecords !== null) {
      return {
        fileName,
        loaded,
        dirty,
        version,
        id,
        appliedMigrations,
        initialVersion,
        isCloudFile,
        shareRecords,
        versionStamp,
        originalVersionStamp,
        deleted,
        isResuming,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} object
 * @return {PlottrFile | Null}
 */
export const validPlottrFileOrNull = (object) => {
  if (object && typeof object === 'object') {
    const file =
      'file' in object && typeof object.file === 'object' && object.file
        ? validFileOrNull(object.file)
        : null
    const ui =
      'ui' in object && typeof object.ui === 'object' && object.ui ? validUiOrNull(object.ui) : null
    const featureFlags =
      'featureFlags' in object && typeof object.featureFlags === 'object' && object.featureFlags
        ? validFeatureFlagsOrNull(object.featureFlags)
        : null
    const series =
      'series' in object && typeof object.series === 'object' && object.series
        ? validSeriesOrNull(object.series)
        : null
    const books =
      'books' in object && typeof object.books === 'object' && object.books
        ? validBooksOrNull(object.books)
        : null
    const beats =
      'beats' in object && typeof object.beats === 'object' && object.beats
        ? validBeatsOrNull(object.beats)
        : null
    const customAttributes =
      'customAttributes' in object &&
      typeof object.customAttributes === 'object' &&
      object.customAttributes
        ? validCustomAttributesOrNull(object.customAttributes)
        : null
    const cards =
      'cards' in object && typeof object.cards === 'object' && object.cards
        ? validCardsOrNull(object.cards, customAttributes?.scenes ?? [])
        : null
    const categories =
      'categories' in object && typeof object.categories === 'object' && object.categories
        ? validCategoriesOrNull(object.categories)
        : null
    const characters =
      'characters' in object && typeof object.characters === 'object' && object.characters
        ? validCharactersOrNull(object.characters)
        : null
    const attributes =
      'attributes' in object && typeof object.attributes === 'object' && object.attributes
        ? validAttributesOrNull(object.attributes)
        : null
    const lines =
      'lines' in object && typeof object.lines === 'object' && object.lines
        ? validLinesOrNull(object.lines)
        : null
    const notes =
      'notes' in object && typeof object.notes === 'object' && object.notes
        ? validNotesOrNull(object.notes, customAttributes?.notes ?? [])
        : null
    const places =
      'places' in object && typeof object.places === 'object' && object.places
        ? validPlacesOrNull(object.places, customAttributes?.places ?? [])
        : null
    const tags =
      'tags' in object && typeof object.tags === 'object' && object.tags
        ? validTagsOrNull(object.tags)
        : null
    const hierarchyLevels =
      'hierarchyLevels' in object &&
      typeof object.hierarchyLevels === 'object' &&
      object.hierarchyLevels
        ? validFullFileHierarchyLevelsOrNull(object.hierarchyLevels)
        : null
    const images =
      'images' in object && typeof object.images === 'object' && object.images
        ? validImagesOrNull(object.images)
        : null
    const audioClips =
      'audioClips' in object && typeof object.audioClips === 'object' && object.audioClips
        ? validAudioClipsOrNull(object.audioClips)
        : null
    const familyTrees =
      'familyTrees' in object && typeof object.familyTrees === 'object' && object.familyTrees
        ? validFamilyTreesOrNull(object.familyTrees)
        : null
    if (
      file !== null &&
      ui !== null &&
      featureFlags !== null &&
      series !== null &&
      books !== null &&
      beats !== null &&
      cards !== null &&
      categories !== null &&
      characters !== null &&
      customAttributes !== null &&
      attributes !== null &&
      lines !== null &&
      notes !== null &&
      places !== null &&
      tags !== null &&
      hierarchyLevels !== null &&
      images !== null &&
      audioClips !== null &&
      familyTrees !== null
    ) {
      return {
        file,
        ui,
        featureFlags,
        series,
        books,
        beats,
        cards,
        categories,
        characters,
        customAttributes,
        attributes,
        lines,
        notes,
        places,
        tags,
        hierarchyLevels,
        images,
        audioClips,
        familyTrees,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}
